import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Play, Square, Clock, ArrowLeft, Users, Tag, Printer, Trash2, X, Globe } from "lucide-react";
import { toast } from "sonner";
import { Link, useLocation } from "react-router-dom";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import WorkOrderMachineTimer from "@/components/WorkOrderMachineTimer";
import { useReactToPrint } from "react-to-print";
import { translations, languageNames } from "@/utils/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to convert UTC time to local time for display
const formatTimeForDisplay = (utcDate, utcTime) => {
  try {
    const dateTime = new Date(`${utcDate}T${utcTime}Z`); // Parse as UTC
    return dateTime.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      hour12: false 
    });
  } catch (e) {
    return utcTime; // Fallback to original if parsing fails
  }
};

// LiveTimer component for Currently Working section
const LiveTimer = ({ startDate, startTime, employeeName, currentEmployee, currentElapsedTime, currentLocalClockInTime }) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    // If this is the current logged-in employee and we have local clock-in time, use it
    const isCurrentEmployee = employeeName === currentEmployee;
    
    const interval = setInterval(() => {
      if (isCurrentEmployee && currentLocalClockInTime) {
        // Use local clock-in time for accurate 00:00:00 start
        const now = new Date();
        const diff = Math.floor((now - currentLocalClockInTime) / 1000);
        setElapsed(diff > 0 ? diff : 0);
      } else {
        // Use server time for other employees or existing sessions
        // Parse as UTC to match server timezone
        const timeParts = startTime.split(':');
        // Construct proper time string
        const timeString = timeParts.length === 3 
          ? startTime  // Already has seconds (HH:MM:SS)
          : `${startTime}:00`;  // Add seconds (HH:MM -> HH:MM:00)
        const start = new Date(`${startDate}T${timeString}Z`); // Parse as UTC with Z suffix
        
        const now = new Date();
        const diff = Math.floor((now - start) / 1000);
        setElapsed(diff > 0 ? diff : 0);
      }
    }, 1000);

    // Initial calculation
    if (isCurrentEmployee && currentLocalClockInTime) {
      const now = new Date();
      const diff = Math.floor((now - currentLocalClockInTime) / 1000);
      setElapsed(diff > 0 ? diff : 0);
    } else {
      // Initial calc for other employees
      const timeParts = startTime.split(':');
      // Construct proper time string
      const timeString = timeParts.length === 3 
        ? startTime  // Already has seconds (HH:MM:SS)
        : `${startTime}:00`;  // Add seconds (HH:MM -> HH:MM:00)
      const start = new Date(`${startDate}T${timeString}Z`); // Parse as UTC
      const now = new Date();
      const diff = Math.floor((now - start) / 1000);
      setElapsed(diff > 0 ? diff : 0);
    }

    return () => clearInterval(interval);
  }, [startDate, startTime, employeeName, currentEmployee, currentLocalClockInTime]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  return (
    <p className="text-xs font-mono font-semibold text-blue-600 mt-1">
      ⏱️ {formatTime(elapsed)}
    </p>
  );
};

const EmployeeTimesheet = () => {
  const [employees, setEmployees] = useState([]);
  const [machines, setMachines] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedMachine, setSelectedMachine] = useState("");
  const [workorderNumber, setWorkorderNumber] = useState("");
  const [activeEntry, setActiveEntry] = useState(null);
  const [activeEmployees, setActiveEmployees] = useState([]);
  const [filteredActiveEmployees, setFilteredActiveEmployees] = useState([]);
  const [machineFilter, setMachineFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [localClockInTime, setLocalClockInTime] = useState(null); // Track exact local clock-in moment
  const [currentDeadline, setCurrentDeadline] = useState(null);
  const [workOrderStatus, setWorkOrderStatus] = useState("in_progress");
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState("");
  const [showProcessDialog, setShowProcessDialog] = useState(false);
  const [availableProcesses, setAvailableProcesses] = useState([]);
  const [availableMachines, setAvailableMachines] = useState([]); // Filtered machines based on work order
  const [processAlreadyCompleted, setProcessAlreadyCompleted] = useState(false); // Track if process is already completed
  const [dialogCountdown, setDialogCountdown] = useState(30); // 30 second countdown for dialog
  const [inactivityCountdown, setInactivityCountdown] = useState(10); // 10 second inactivity timeout for work page
  const [showInactivityWarning, setShowInactivityWarning] = useState(false); // Show warning before auto-return
  
  // Language selection for mobile
  const [language, setLanguage] = useState(() => {
    // Try to get saved language from localStorage
    const saved = localStorage.getItem('employeeLanguage');
    return saved || 'en';
  });
  const t = translations[language] || translations.en; // Translation helper
  
  // Mobile/PWA detection
  const [isMobile, setIsMobile] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  
  useEffect(() => {
    // Detect mobile device (match Landing.jsx pattern)
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || 
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Detect PWA (installed as app)
    const checkPWA = () => {
      setIsPWA(window.matchMedia('(display-mode: standalone)').matches || 
               window.navigator.standalone === true);
    };
    checkPWA();
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Material Tracking Dialog State (for Corrugation & Sheet Cutting completion)
  const [showMaterialTrackingDialog, setShowMaterialTrackingDialog] = useState(false);
  const [materialDialogCountdown, setMaterialDialogCountdown] = useState(120); // 2 minute countdown for material dialog
  const [materialTrackingData, setMaterialTrackingData] = useState([]);
  const [availableReels, setAvailableReels] = useState([]);
  const [workOrderReels, setWorkOrderReels] = useState([]); // Reels assigned to the work order
  const [pendingStopData, setPendingStopData] = useState(null); // Store stop data while showing material dialog
  const [corrugatedRollsCreated, setCorrugatedRollsCreated] = useState(""); // Track total corrugated rolls created from parent reels
  
  // Production Counting State (for various machine processes)
  const [productionCounts, setProductionCounts] = useState({
    sheetCounting: "",      // M002 - Sheet Cutting (P2A)
    paperCounting: "",      // M002 - Paper Cutting (P2B)
    balanceReelWeight: "",  // M002 - Paper Cutting Balance Reel Weight
    boardCountingP3: "",    // M003 - Sheet Pasting Board Counting
    boardCountingP4: "",    // M004 - Thin Blade Slitter Board Counting
    boardCountingP5: "",    // M005 - 4 Bar Rotary Board Counting
    boardCountingP6: "",    // M006 - RS4 Printing Board Counting
    boardCountingP7: "",    // M007 - Flatbed Punching Board Counting
    boardCountingP8: "",    // M008 - Rotary Die Punching Board Counting
    boardCountingP9: "",    // M009 - Calico Pasting Board Counting
    totalBoxesP10: "",      // M010 - Stitching & Bundling Total Boxes
    loadingCountP11: "",    // M011 - Loading / Unloading Count
    transportCountP12: ""   // M012 - Transport Count
  });
  
  // Track which sub-process is selected for M002 (Sheet Cutting or Paper Cutting)
  const [m002SubProcess, setM002SubProcess] = useState("sheet_cutting"); // "sheet_cutting" or "paper_cutting"
  
  // P2B reel info for auto-calculating used weight from paper counting
  const [p2bReelInfo, setP2bReelInfo] = useState(null); // { gsm, reelSize, cuttingSize, originalWeight, reelId }
  
  // Extract GSM from RM name (e.g., "NS12016" → 120)
  const extractGSMFromRmName = (rmName) => {
    if (!rmName) return 0;
    const match = rmName.match(/[A-Za-z]+(\d{3})\d{2}/);
    return match ? parseInt(match[1]) : 0;
  };

  // Calculate P2B paper weight and balance
  const p2bCalc = useMemo(() => {
    if (!p2bReelInfo || !productionCounts.paperCounting) return null;
    const { gsm, reelSize, cuttingSize, originalWeight } = p2bReelInfo;
    if (!gsm || !reelSize || !cuttingSize) return null;
    const paperCount = parseInt(productionCounts.paperCounting) || 0;
    if (paperCount <= 0) return null;
    // Paper weight in kg per sheet
    const paperWeightKg = (reelSize * cuttingSize * gsm) / 1550 / 1000;
    // Total papers that can be produced from this reel
    const totalPapersPossible = Math.floor(originalWeight / paperWeightKg);
    // Used weight
    const usedWeightKg = paperCount * paperWeightKg;
    // Balance papers
    const balancePapers = Math.max(0, totalPapersPossible - paperCount);
    // Balance reel weight
    const balanceWeightKg = Math.max(0, balancePapers * paperWeightKg);
    return {
      paperWeightKg: paperWeightKg.toFixed(4),
      paperWeightGrams: (paperWeightKg * 1000).toFixed(2),
      totalPapersPossible,
      usedWeightKg: usedWeightKg.toFixed(2),
      balancePapers,
      balanceWeightKg: balanceWeightKg.toFixed(2),
      gsm,
      reelSize,
      cuttingSize,
    };
  }, [p2bReelInfo, productionCounts.paperCounting]);
  
  // Label Printing Dialog State (for bit reels ready to be labeled)
  const [showLabelDialog, setShowLabelDialog] = useState(false);
  const [bitReelsForLabel, setBitReelsForLabel] = useState([]);
  const [loadingBitReels, setLoadingBitReels] = useState(false);
  const labelPrintRef = useRef(null);
  const [selectedLabelReel, setSelectedLabelReel] = useState(null);
  const [pendingLabelsCount, setPendingLabelsCount] = useState(0);
  const [oldestUnprintedDays, setOldestUnprintedDays] = useState(0);
  
  // Map processes to machines
  const processMachineMap = {
    'process1': 'Corrugation',
    'process2a': 'Sheet Cutting',       // Will match "Sheet Cutting Machine (P2A)"
    'process2b': 'Paper Cutting',       // Will match "Paper Cutting Machine (P2B)"
    'process2': 'Sheet / Paper Cutting', // Legacy support
    'process3': 'Sheet Pasting',
    'process4': 'Thin Blade Slitter Scorer',
    'process5': '4 Bar Rotary Creasing',
    'process6': 'RS4 Single Color Printing & Slotting',
    'process6a': 'Eccentric Slotter',
    'process7': 'Flat bed Punching',
    'process8': 'Rotary Die Punching',
    'process9': 'Calico Pasting',
    'process9a': 'Board Drying',
    'process10': 'Stitching & Bundling',
    'process11': 'Loading / Unloading',
    'process12': 'Transport'
  };

  useEffect(() => {
    // Clear saved employee on fresh navigation to start new session
    // This ensures clicking Home and coming back starts fresh
    const navigationEntries = performance.getEntriesByType("navigation");
    const navigationType = navigationEntries.length > 0 ? navigationEntries[0].type : "navigate";
    
    // Clear localStorage on navigate (not reload)
    if (navigationType === "navigate") {
      localStorage.removeItem('selectedEmployee');
      setSelectedEmployee("");
      setActiveEntry(null);
    } else {
      // Only restore on page refresh
      const savedEmployee = localStorage.getItem('selectedEmployee');
      if (savedEmployee) {
        setSelectedEmployee(savedEmployee);
        checkActiveEntry(savedEmployee);
      }
    }
    
    fetchEmployees();
    fetchMachines();
    fetchWorkOrders();
    fetchActiveEmployees();
    
    // Refresh active employees list every 10 seconds
    const interval = setInterval(fetchActiveEmployees, 10000);
    return () => clearInterval(interval);
  }, []);
  // Orphaned session cleanup removed

  // 30 second countdown timer for status dialog
  useEffect(() => {
    let countdownInterval;
    if (showStatusDialog) {
      setDialogCountdown(30); // Reset to 30 seconds when dialog opens
      countdownInterval = setInterval(() => {
        setDialogCountdown(prev => {
          if (prev <= 1) {
            // Time's up - close dialog and return to main page
            setShowStatusDialog(false);
            setWorkOrderStatus("in_progress");
            toast.info("Time expired. Returning to start page.");
            return 30;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, [showStatusDialog]);

  // 2 minute countdown timer for material tracking dialog
  useEffect(() => {
    let countdownInterval;
    if (showMaterialTrackingDialog) {
      setMaterialDialogCountdown(120); // Reset to 120 seconds (2 mins) when dialog opens
      countdownInterval = setInterval(() => {
        setMaterialDialogCountdown(prev => {
          if (prev <= 1) {
            // Time's up - close material dialog and skip material entry
            setShowMaterialTrackingDialog(false);
            toast.info("Material entry time expired.");
            if (pendingStopData) {
              confirmStopDirect(pendingStopData.status);
              setPendingStopData(null);
            }
            return 120;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, [showMaterialTrackingDialog]);


  // Inactivity timeout for work in progress page - 10 seconds
  useEffect(() => {
    let inactivityTimer;
    let countdownTimer;
    
    const resetInactivityTimer = () => {
      setShowInactivityWarning(false);
      setInactivityCountdown(10);
      
      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (countdownTimer) clearInterval(countdownTimer);
      
      // Only set timer if we have an active entry (work in progress)
      if (activeEntry && !showStatusDialog) {
        // Start inactivity timer
        inactivityTimer = setTimeout(() => {
          setShowInactivityWarning(true);
          // Start countdown
          countdownTimer = setInterval(() => {
            setInactivityCountdown(prev => {
              if (prev <= 1) {
                // Return to start page
                setActiveEntry(null);
                setLocalClockInTime(null);
                setSelectedEmployee("");
                setSelectedMachine("");
                setWorkorderNumber("");
                setElapsedTime(0);
                setShowInactivityWarning(false);
                toast.info("Session timed out due to inactivity. Returning to start page.");
                localStorage.removeItem('selectedEmployee');
                return 10;
              }
              return prev - 1;
            });
          }, 1000);
        }, 10000); // Show warning after 10 seconds of inactivity
      }
    };
    
    // Reset timer on user activity
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach(event => {
      window.addEventListener(event, resetInactivityTimer);
    });
    
    // Initial timer start
    resetInactivityTimer();
    
    return () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (countdownTimer) clearInterval(countdownTimer);
      activityEvents.forEach(event => {
        window.removeEventListener(event, resetInactivityTimer);
      });
    };
  }, [activeEntry, showStatusDialog]);

  useEffect(() => {
    // Apply machine filter to active employees
    if (machineFilter === "all") {
      setFilteredActiveEmployees(activeEmployees);
    } else {
      setFilteredActiveEmployees(
        activeEmployees.filter(emp => emp.machine_id === machineFilter)
      );
    }
  }, [machineFilter, activeEmployees]);

  useEffect(() => {
    let interval;
    if (activeEntry) {
      // Function to calculate elapsed time
      const calculateElapsed = () => {
        // If we have a local clock-in time (fresh session), use it for accurate 00:00:00 start
        if (localClockInTime) {
          const now = new Date();
          const diff = Math.floor((now - localClockInTime) / 1000);
          setElapsedTime(diff > 0 ? diff : 0);
        } else {
          // Use server time for existing sessions (page reload)
          // Parse as UTC to match server timezone
          const startTimeParts = activeEntry.start_time.split(':');
          // Construct proper time string
          const timeString = startTimeParts.length === 3 
            ? activeEntry.start_time  // Already has seconds (HH:MM:SS)
            : `${activeEntry.start_time}:00`;  // Add seconds (HH:MM -> HH:MM:00)
          const startDate = new Date(`${activeEntry.date}T${timeString}Z`); // Parse as UTC
          
          const now = new Date();
          const diff = Math.floor((now - startDate) / 1000);
          setElapsedTime(diff > 0 ? diff : 0);
        }
      };
      
      // Start with 0 for fresh clock-ins
      if (localClockInTime) {
        setElapsedTime(0);
      }
      
      // Calculate immediately
      calculateElapsed();
      
      // Then update every second
      interval = setInterval(calculateElapsed, 1000);
    } else {
      setElapsedTime(0);
      setLocalClockInTime(null); // Clear local time when no active entry
    }
    return () => clearInterval(interval);
  }, [activeEntry, localClockInTime]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    const wsUrl = BACKEND_URL.replace('http://', 'ws://').replace('https://', 'wss://');
    const ws = new WebSocket(`${wsUrl}/ws/timesheet`);
    
    ws.onopen = () => {
      // Send periodic pings to keep connection alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000); // Ping every 30 seconds
      
      // Store interval ID in ws object for cleanup
      ws.pingInterval = pingInterval;
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'clock_in') {
          // Refresh active employees when someone clocks in
          fetchActiveEmployees();
          
          // If it's the current employee clocking in, update their active entry
          if (selectedEmployee && data.employee_name === selectedEmployee) {
            // For clock-in from another device, we need to reconstruct the clock-in time
            // Parse the server's UTC time and set it as localClockInTime
            if (data.start_time && data.date) {
              const startTimeParts = data.start_time.split(':');
              // Construct proper time string (HH:MM:SS format)
              const timeString = startTimeParts.length === 3 
                ? data.start_time  // Already has seconds
                : `${data.start_time}:00`;  // Add seconds
              const serverClockInTime = new Date(`${data.date}T${timeString}Z`); // Parse as UTC
              setLocalClockInTime(serverClockInTime);
              // Preserve the local time we just set
              checkActiveEntry(selectedEmployee, true);
            } else {
              checkActiveEntry(selectedEmployee);
            }
          }
        } else if (data.type === 'clock_out') {
          // Refresh active employees when someone clocks out
          fetchActiveEmployees();
          
          // If it's the current employee clocking out, clear their active entry
          if (selectedEmployee && data.employee_name === selectedEmployee) {
            setLocalClockInTime(null);
            checkActiveEntry(selectedEmployee);
          }
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };
    
    let errorLogged = false;
    ws.onerror = () => {
      // Suppress repeated WebSocket reconnect noise — log only the first failure per connection.
      if (!errorLogged) {
        errorLogged = true;
        console.warn('Realtime updates disconnected — will reconnect automatically');
      }
    };
    
    ws.onclose = () => {
      if (ws.pingInterval) {
        clearInterval(ws.pingInterval);
      }
    };
    
    // Cleanup on unmount
    return () => {
      if (ws.pingInterval) {
        clearInterval(ws.pingInterval);
      }
      ws.close();
    };
  }, [selectedEmployee]); // Re-establish connection if selected employee changes

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error("Failed to load employees");
    }
  };

  const fetchMachines = async () => {
    try {
      const response = await axios.get(`${API}/machines`);
      setMachines(response.data);
      setAvailableMachines(response.data); // Initialize available machines with all machines
    } catch (error) {
      console.error("Error fetching machines:", error);
      toast.error("Failed to load machines");
    }
  };

  const fetchWorkOrders = async () => {
    try {
      // Fetch only new work orders
      const response = await axios.get(`${API}/work-orders`);
      
      // Filter new work orders by status: show only "available" and "in_progress"
      // Hide: "completed", "rejected", and "delivered"
      const newWorkOrders = response.data
        .filter(wo => wo.status === "available" || wo.status === "in_progress")
        .map(wo => ({
          work_order_number: wo.header.work_order_number,
          po_number: wo.header.po_number || "",
          customer_name: wo.header.customer_name,
          item_name: wo.header.item_name,
          processes: wo.processes // Include processes for filtering
        }));
      
      setWorkOrders(newWorkOrders);
    } catch (error) {
      console.error("Error fetching work orders:", error);
      toast.error("Failed to load work orders");
    }
  };
  const handleWorkOrderChange = async (value) => {
    if (value !== "placeholder") {
      setWorkorderNumber(value);
      
      // Fetch work order details to filter machines
      try {
        const response = await axios.get(`${API}/work-orders/by-number/${value}`);
        const workOrder = response.data;
        
        // Get list of selected processes
        const selectedProcs = [];
        if (workOrder.processes) {
          // Check numbered processes 1-12
          for (let i = 1; i <= 12; i++) {
            const processKey = `process${i}`;
            const process = workOrder.processes[processKey];
            if (process && typeof process === 'object' && Object.keys(process).length > 0) {
              selectedProcs.push(processKey);
            }
          }
          
          // Also check for new split processes (process2a, process2b, process9a)
          if (workOrder.processes.process2a && typeof workOrder.processes.process2a === 'object' && Object.keys(workOrder.processes.process2a).length > 0) {
            selectedProcs.push('process2a');
          }
          if (workOrder.processes.process2b && typeof workOrder.processes.process2b === 'object' && Object.keys(workOrder.processes.process2b).length > 0) {
            selectedProcs.push('process2b');
          }
          // Check for process9a (Board Drying)
          if (workOrder.processes.process9a && typeof workOrder.processes.process9a === 'object' && Object.keys(workOrder.processes.process9a).length > 0) {
            selectedProcs.push('process9a');
          }
        }
        
        // Also check selected_processes flag
        if (workOrder.selected_processes) {
          if (workOrder.selected_processes.process2a && !selectedProcs.includes('process2a')) {
            selectedProcs.push('process2a');
          }
          if (workOrder.selected_processes.process2b && !selectedProcs.includes('process2b')) {
            selectedProcs.push('process2b');
          }
          if (workOrder.selected_processes.process9a && !selectedProcs.includes('process9a')) {
            selectedProcs.push('process9a');
          }
        }
        
        // Always include process11 and process12 (mandatory processes)
        if (!selectedProcs.includes('process11')) selectedProcs.push('process11');
        if (!selectedProcs.includes('process12')) selectedProcs.push('process12');
        
        // Map processes to machine names
        const requiredMachineNames = selectedProcs.map(proc => processMachineMap[proc]).filter(Boolean);
        
        // Add Sheet Cutting and Paper Cutting machines if legacy process2 is selected
        // This ensures backwards compatibility with old work orders
        if (selectedProcs.includes('process2')) {
          requiredMachineNames.push('Sheet Cutting');  // Will match M002A
          requiredMachineNames.push('Paper Cutting');  // Will match M002B
        }
        
        // Filter machines to only show relevant ones
        const filteredMachines = machines.filter(machine => 
          requiredMachineNames.some(name => machine.name.includes(name) || name.includes(machine.name))
        );
        
        
        setAvailableMachines(filteredMachines);
        
        if (filteredMachines.length === 0) {
          toast.warning("No machines found for the selected processes in this work order");
        }
      } catch (error) {
        console.error("Error fetching work order details:", error);
        // On error, show all machines
        setAvailableMachines(machines);
      }
    }
  };

  const fetchActiveEmployees = async () => {
    try {
      const response = await axios.get(`${API}/timesheet?status=ongoing`);
      // Filter to show only truly active sessions (end_time is null)
      const activeOnly = response.data.filter(entry => !entry.end_time);
      setActiveEmployees(activeOnly);
    } catch (error) {
      console.error("Error fetching active employees:", error);
    }
  };

  // Fetch available reels for material tracking
  const fetchAvailableReels = async () => {
    try {
      const response = await axios.get(`${API}/inventory?status=available`);
      setAvailableReels(response.data || []);
    } catch (error) {
      console.error("Error fetching available reels:", error);
    }
  };

  // Fetch reels assigned to a work order from process1 (Corrugation), process2 (Sheet Cutting), and process3 (Sheet Pasting)
  // machineId parameter filters which process materials to return
  const fetchWorkOrderReels = async (woNumber, machineId) => {
    try {
      const woResponse = await axios.get(`${API}/work-orders`);
      const workOrder = woResponse.data.find(wo => 
        wo.header?.work_order_number === woNumber
      );
      
      if (!workOrder) return { reels: [], reelIds: [], reelMeta: [] };
      
      const reelIds = [];
      const reelMeta = []; // { id, label, source }
      const processes = workOrder.processes || {};
      
      // Get reels from process1 (Corrugation) - has rows with base_reel_id and flute_reel_id
      // Only include if machine is M001 (Corrugation)
      if ((!machineId || machineId === "M001") && processes.process1?.rows) {
        let baseCount = 0;
        let fluteCount = 0;
        processes.process1.rows.forEach(row => {
          if (row.base_reel_id) {
            baseCount++;
            reelIds.push(row.base_reel_id);
            reelMeta.push({ 
              id: row.base_reel_id, 
              label: `Base Reel #${baseCount}`, 
              source: "corrugation",
              weight: row.base_weight, // Store assigned weight
              reelType: "base" // Mark as base reel for RM Name suffix
            });
          }
          if (row.flute_reel_id) {
            fluteCount++;
            reelIds.push(row.flute_reel_id);
            reelMeta.push({ 
              id: row.flute_reel_id, 
              label: `Flute Reel #${fluteCount}`, 
              source: "corrugation",
              weight: row.flute_weight,
              reelType: "flute" // Mark as flute reel for RM Name suffix
            });
          }
        });
      }
      
      // Get reels from process2a (Sheet Cutting) - new split process
      // Only include if machine is M002 or M002A (Sheet Cutting)
      if ((!machineId || machineId === "M002" || machineId === "M002A") && processes.process2a) {
        if (processes.process2a.reels && Array.isArray(processes.process2a.reels)) {
          processes.process2a.reels.forEach((r, idx) => {
            if (r.reel_no) {
              reelIds.push(r.reel_no);
              reelMeta.push({ 
                id: r.reel_no, 
                label: `Sheet Cutting Reel #${idx + 1}`, 
                source: "sheet_cutting",
                weight: r.reel_weight,
                size: r.reel_size
              });
            }
          });
        } else if (processes.process2a.reel_no) {
          reelIds.push(processes.process2a.reel_no);
          reelMeta.push({ 
            id: processes.process2a.reel_no, 
            label: "Sheet Cutting Reel #1", 
            source: "sheet_cutting",
            weight: processes.process2a.reel_weight,
            size: processes.process2a.reel_size
          });
        }
      }
      
      // Get reels from process2b (Paper Cutting) - new split process
      // Only include if machine is M002 or M002B (Paper Cutting)
      if ((!machineId || machineId === "M002" || machineId === "M002B") && processes.process2b) {
        if (processes.process2b.reels && Array.isArray(processes.process2b.reels)) {
          processes.process2b.reels.forEach((r, idx) => {
            if (r.reel_no) {
              reelIds.push(r.reel_no);
              reelMeta.push({ 
                id: r.reel_no, 
                label: `Paper Cutting Reel #${idx + 1}`, 
                source: "paper_cutting",
                weight: r.reel_weight,
                size: r.reel_size
              });
            }
          });
        } else if (processes.process2b.reel_no) {
          reelIds.push(processes.process2b.reel_no);
          reelMeta.push({ 
            id: processes.process2b.reel_no, 
            label: "Paper Cutting Reel #1", 
            source: "paper_cutting",
            weight: processes.process2b.reel_weight,
            size: processes.process2b.reel_size
          });
        }
      }
      
      // Legacy: Get reels from process2 (Sheet/Paper Cutting combined) - old format
      // Only include if machine is M002 and no new process2a/process2b exists
      if ((!machineId || machineId === "M002" || machineId === "M002A" || machineId === "M002B") && processes.process2 && !processes.process2a && !processes.process2b) {
        if (processes.process2.reels && Array.isArray(processes.process2.reels)) {
          processes.process2.reels.forEach((r, idx) => {
            if (r.reel_no) {
              reelIds.push(r.reel_no);
              reelMeta.push({ 
                id: r.reel_no, 
                label: `Sheet / Paper Cutting Reel #${idx + 1}`, 
                source: "sheet_cutting",
                weight: r.reel_weight,
                size: r.reel_size
              });
            }
          });
        } else if (processes.process2.reel_no) {
          reelIds.push(processes.process2.reel_no);
          reelMeta.push({ 
            id: processes.process2.reel_no, 
            label: "Sheet / Paper Cutting Reel #1", 
            source: "sheet_cutting",
            weight: processes.process2.reel_weight,
            size: processes.process2.reel_size
          });
        }
      }
      
      // Get materials from process3 (Sheet Pasting) - may have stock_id from top_paper_types array or single stock_id
      // Only include if machine is M003 (Sheet Pasting)
      if ((!machineId || machineId === "M003") && processes.process3) {
        const addedStockIds = new Set(); // Track added stock IDs to avoid duplicates
        
        // New structure: top_paper_types is an array with { top_paper_type, stock_id, qty }
        if (processes.process3.top_paper_types && Array.isArray(processes.process3.top_paper_types)) {
          processes.process3.top_paper_types.forEach((paper, idx) => {
            if (paper.stock_id && !addedStockIds.has(paper.stock_id)) {
              addedStockIds.add(paper.stock_id);
              reelIds.push(paper.stock_id);
              reelMeta.push({ 
                id: paper.stock_id, 
                label: `${paper.top_paper_type || 'Top Paper'} #${idx + 1}`, 
                source: "sheet_pasting",
                qty: paper.qty, // Store the qty from work order
                isPrintedPaper: true // Flag to identify this needs Qty tracking, not weight
              });
            }
          });
        }
        
        // Legacy: If process3 has stock_id (single value) - only add if not already in top_paper_types
        if (processes.process3.stock_id && !addedStockIds.has(processes.process3.stock_id)) {
          addedStockIds.add(processes.process3.stock_id);
          reelIds.push(processes.process3.stock_id);
          // Try to get the top_paper_type name if available
          const topPaperLabel = processes.process3.top_paper_type || "Top Paper";
          reelMeta.push({ 
            id: processes.process3.stock_id, 
            label: topPaperLabel, 
            source: "sheet_pasting",
            qty: processes.process3.sheet_counting, // Use sheet_counting as qty for legacy format
            isPrintedPaper: true
          });
        }
        
        // Legacy: If process3 has rm_name (old format) - only if no stock_id
        if (processes.process3.rm_name && !processes.process3.stock_id && !addedStockIds.has(processes.process3.rm_name)) {
          reelIds.push(processes.process3.rm_name);
          reelMeta.push({ id: processes.process3.rm_name, label: "Sheet Pasting Material", source: "sheet_pasting" });
        }
      }
      
      // Get available reels matching these IDs
      // Also check for split stock IDs (e.g., RMCPI008-SPL1 is a split from RMCPI008)
      const inventoryResponse = await axios.get(`${API}/inventory`);
      const matchingReels = inventoryResponse.data.filter(item => {
        // Direct match by reel_id or rm_name
        const directMatch = reelIds.includes(item.reel_id) || reelIds.includes(item.rm_name);
        
        // Match split stock by parent_reel_id
        const splitMatch = item.is_split_stock && reelIds.includes(item.parent_reel_id);
        
        // Only include available stock (not already fully used)
        const isAvailable = item.status === "available";
        
        return (directMatch || splitMatch) && isAvailable;
      });
      
      // If no available stock found with direct match, also check parent match for qty-based items
      // This handles cases where original stock was used and split stock was created
      if (matchingReels.length === 0 && reelIds.length > 0) {
        // Try to find any split stock that came from our original stock IDs
        const splitStockMatches = inventoryResponse.data.filter(item => 
          item.is_split_stock && 
          reelIds.includes(item.parent_reel_id) &&
          item.status === "available"
        );
        
        if (splitStockMatches.length > 0) {
          // Update the reelMeta to use split stock IDs
          splitStockMatches.forEach(splitItem => {
            // Find the meta entry for this parent and update it
            const metaIdx = reelMeta.findIndex(m => m.id === splitItem.parent_reel_id);
            if (metaIdx >= 0) {
              reelMeta[metaIdx].id = splitItem.reel_id;
              reelMeta[metaIdx].qty = splitItem.qty; // Update qty from split stock
            }
          });
          
          setWorkOrderReels(splitStockMatches);
          return { reels: splitStockMatches, reelIds: splitStockMatches.map(s => s.reel_id), reelMeta };
        }
      }
      
      setWorkOrderReels(matchingReels);
      return { reels: matchingReels, reelIds, reelMeta };
    } catch (error) {
      console.error("Error fetching work order reels:", error);
      return { reels: [], reelIds: [], reelMeta: [] };
    }
  };

  // Fetch bit reels that are ready for label printing
  // These are bit reels created from Process 1 (Corrugation) or Process 2 (Sheet Cutting)
  const fetchBitReelsForLabel = async (updateCountOnly = false) => {
    if (!updateCountOnly) setLoadingBitReels(true);
    try {
      const response = await axios.get(`${API}/inventory`);
      // Filter for bit reels that are available, have a parent, and label not yet printed
      const bitReels = response.data.filter(item => 
        item.is_bit_reel === true && 
        item.status === "available" &&
        item.parent_reel_id && // Must have a parent
        item.label_printed !== true // Label not yet printed
      );
      
      // Calculate oldest unprinted days
      let maxDays = 0;
      const now = new Date();
      bitReels.forEach(reel => {
        if (reel.created_at || reel.in_date) {
          const createdDate = new Date(reel.created_at || reel.in_date);
          const daysDiff = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
          if (daysDiff > maxDays) maxDays = daysDiff;
        }
      });
      
      // Update counts
      setPendingLabelsCount(bitReels.length);
      setOldestUnprintedDays(maxDays);
      
      if (!updateCountOnly) {
        // Enrich with parent data
        const enrichedBitReels = await Promise.all(bitReels.map(async (bitReel) => {
          // Find parent reel
          const parentReel = response.data.find(item => item.reel_id === bitReel.parent_reel_id);
          // Calculate days since creation
          const createdDate = new Date(bitReel.created_at || bitReel.in_date || new Date());
          const daysSinceCreation = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
          
          return {
            ...bitReel,
            parent_weight: bitReel.original_weight || parentReel?.reel_weight || 0,
            parent_rm_name: parentReel?.rm_name || bitReel.rm_name || "",
            parent_reel_size: parentReel?.reel_size || bitReel.reel_size || "",
            days_pending: daysSinceCreation,
          };
        }));
        
        setBitReelsForLabel(enrichedBitReels);
      }
    } catch (error) {
      console.error("Error fetching bit reels for label:", error);
      if (!updateCountOnly) toast.error("Failed to fetch bit reels");
    } finally {
      if (!updateCountOnly) setLoadingBitReels(false);
    }
  };

  // Fetch pending labels count on component mount
  useEffect(() => {
    fetchBitReelsForLabel(true); // Only update count on initial load
  }, []);

  // Handle opening label dialog
  const handleOpenLabelDialog = () => {
    fetchBitReelsForLabel();
    setShowLabelDialog(true);
  };

  // Handle marking label as printed
  const handleMarkLabelPrinted = async (reelId) => {
    try {
      await axios.put(`${API}/inventory/${reelId}/label-printed`);
      toast.success("Label marked as printed");
      // Refresh the list
      fetchBitReelsForLabel();
    } catch (error) {
      console.error("Error marking label as printed:", error);
      toast.error("Failed to mark label as printed");
    }
  };

  // Handle print for a specific label
  const handlePrintLabel = useReactToPrint({
    contentRef: labelPrintRef,
    documentTitle: selectedLabelReel ? `Label-${selectedLabelReel.reel_id}` : "Label",
  });

  // Add a material tracking row
  const addMaterialRow = () => {
    setMaterialTrackingData([
      ...materialTrackingData,
      { id: Date.now(), reelId: "", usedWeight: "", balanceWeight: "", reelData: null }
    ]);
  };

  // Remove a material tracking row
  const removeMaterialRow = (id) => {
    setMaterialTrackingData(materialTrackingData.filter(row => row.id !== id));
  };

  // Update material tracking row
  const updateMaterialRow = (id, field, value) => {
    setMaterialTrackingData(materialTrackingData.map(row => {
      if (row.id === id) {
        if (field === "reelId") {
          const reel = workOrderReels.find(r => r.reel_id === value || r.id === value);
          return { ...row, reelId: value, reelData: reel, balanceWeight: "", usedWeight: "" };
        }
        if (field === "balanceWeight") {
          const originalWeight = parseFloat(row.reelData?.reel_weight) || 0;
          const balance = parseFloat(value) || 0;
          const used = Math.max(0, originalWeight - balance);
          return { ...row, balanceWeight: value, usedWeight: used > 0 ? used.toFixed(2) : "" };
        }
        // Handle balanceQty for printed paper - auto-calculate usedQty
        if (field === "balanceQty") {
          const originalQty = parseFloat(row.reelData?.qty) || parseFloat(row.assignedQty) || 0;
          const balance = parseFloat(value) || 0;
          const used = Math.max(0, originalQty - balance);
          return { ...row, balanceQty: value, usedQty: used > 0 ? used.toString() : "0" };
        }
        // Handle usedQty for printed paper - auto-calculate balanceQty
        if (field === "usedQty") {
          const originalQty = parseFloat(row.reelData?.qty) || parseFloat(row.assignedQty) || 0;
          const used = parseFloat(value) || 0;
          const balance = Math.max(0, originalQty - used);
          return { ...row, usedQty: value, balanceQty: balance > 0 ? balance.toString() : "0" };
        }
        return { ...row, [field]: value };
      }
      return row;
    }));
  };

  // Submit material tracking and create bit reels if needed
  const submitMaterialTracking = async () => {
    setLoading(true);
    try {
      // Process each material row
      for (const row of materialTrackingData) {
        // Handle Printed Paper (Qty-based tracking)
        if (row.isPrintedPaper) {
          if (!row.reelId) continue;
          
          const usedQty = parseFloat(row.usedQty) || 0;
          const balanceQty = parseFloat(row.balanceQty) || 0;
          
          // Find the item in available reels
          const item = workOrderReels.find(r => r.reel_id === row.reelId || r.id === row.reelId);
          if (!item) continue;
          
          if (balanceQty > 0) {
            // Create new stock for balance qty (similar to bit reel but for qty)
            await axios.post(`${API}/inventory/split-stock`, {
              original_item_id: item.id,
              used_qty: usedQty,
              balance_qty: balanceQty,
              used_comment: `Used in ${pendingStopData?.workorderNumber} on ${activeEntry?.machine_name}`,
              work_order_number: pendingStopData?.workorderNumber || workorderNumber
            });
            toast.success(`Balance stock created for ${row.reelId} with ${balanceQty} Nos`);
          } else {
            // Mark item as fully used
            await axios.put(`${API}/inventory/${item.id}`, {
              status: "stock_used",
              used_comment: `Fully used in ${pendingStopData?.workorderNumber} on ${activeEntry?.machine_name}`,
              used_in_work_order: pendingStopData?.workorderNumber || workorderNumber
            });
            toast.success(`Stock ${row.reelId} marked as fully used`);
          }
        } else if (row.isCorrugatedRoll) {
          // Handle Corrugated Roll (Qty-based tracking)
          if (!row.reelId) continue;
          
          const usedQty = parseFloat(row.usedQty) || 0;
          const balanceQty = parseFloat(row.balanceQty) || 0;
          
          // Find the corrugated roll item in inventory
          const item = workOrderReels.find(r => r.reel_id === row.reelId || r.id === row.reelId);
          if (!item) continue;
          
          if (balanceQty > 0) {
            // Update the corrugated roll qty in inventory
            await axios.put(`${API}/inventory/${item.id}`, {
              qty: balanceQty,
              used_comment: `${usedQty} rolls used in ${pendingStopData?.workorderNumber} on ${activeEntry?.machine_name}`,
              used_in_work_order: pendingStopData?.workorderNumber || workorderNumber
            });
            toast.success(`Corrugated Roll ${row.reelId} updated: ${balanceQty} rolls remaining`);
          } else {
            // Mark corrugated roll as fully used
            await axios.put(`${API}/inventory/${item.id}`, {
              qty: 0,
              status: "stock_used",
              used_comment: `Fully used (${usedQty} rolls) in ${pendingStopData?.workorderNumber} on ${activeEntry?.machine_name}`,
              used_in_work_order: pendingStopData?.workorderNumber || workorderNumber
            });
            toast.success(`Corrugated Roll ${row.reelId} marked as fully used`);
          }
        } else {
          // Handle Weight-based tracking (regular reels)
          if (!row.reelId || !row.usedWeight) continue;
          
          const usedWeight = parseFloat(row.usedWeight) || 0;
          const balanceWeight = parseFloat(row.balanceWeight) || 0;
          
          // Find the reel in available reels
          const reel = workOrderReels.find(r => r.reel_id === row.reelId || r.id === row.reelId);
          if (!reel) continue;
          
          if (balanceWeight > 0) {
            // Create bit reel for balance
            // Determine reel type for RM Name suffix (-B for base, -F for flute)
            const reelType = row.reelType || null;
            
            await axios.post(`${API}/inventory/bit-reel`, {
              original_item_id: reel.id,
              used_weight: usedWeight,
              balance_weight: balanceWeight,
              used_comment: `Used in ${pendingStopData?.workorderNumber} on ${activeEntry?.machine_name}`,
              work_order_number: pendingStopData?.workorderNumber || workorderNumber,
              reel_type: reelType // "base" or "flute" for RM Name suffix
            });
            toast.success(`Bit reel created for ${row.reelId} with ${balanceWeight} kg balance`);
          } else {
            // Mark reel as fully used
            await axios.put(`${API}/inventory/${reel.id}`, {
              status: "stock_used",
              used_comment: `Fully used in ${pendingStopData?.workorderNumber} on ${activeEntry?.machine_name}`,
              used_in_work_order: pendingStopData?.workorderNumber || workorderNumber
            });
            toast.success(`Reel ${row.reelId} marked as fully used`);
          }
        }
      }
      
      // Store tracked reel IDs in work order for cross-machine tracking
      const trackedReelIds = materialTrackingData
        .filter(row => row.reelId && (row.usedWeight || row.usedQty))
        .map(row => row.reelId);
      if (trackedReelIds.length > 0) {
        const woNum = pendingStopData?.workorderNumber || workorderNumber;
        await axios.patch(`${API}/work-orders/${woNum}/update-material-tracked-reels`, trackedReelIds);
      }
      
      // Store corrugated rolls created (only for Corrugation - M001)
      if (activeEntry?.machine_id === "M001" && corrugatedRollsCreated) {
        const woNum = pendingStopData?.workorderNumber || workorderNumber;
        const rollsCount = parseInt(corrugatedRollsCreated) || 0;
        if (rollsCount > 0) {
          try {
            // Get the parent reel IDs from the material tracking data
            const parentReels = materialTrackingData
              .filter(row => row.reelId)
              .map(row => row.reelId);
            
            await axios.patch(`${API}/work-orders/${woNum}/update-corrugated-rolls`, {
              corrugated_rolls_created: rollsCount,
              employee_name: activeEntry?.employee_name || selectedEmployee,
              parent_reels: parentReels
            });
            toast.success(`${rollsCount} Corrugated Rolls added to inventory!`);
          } catch (error) {
            console.error("Error saving corrugated rolls count:", error);
          }
        }
      }
      
      // Save P1 (Corrugation) material usage data to work order for Production Tracking
      if (activeEntry?.machine_id === "M001" && materialTrackingData.length > 0) {
        const woNum = pendingStopData?.workorderNumber || workorderNumber;
        try {
          // Calculate total used and balance weights from all rows
          let totalUsedBase = 0;
          let totalUsedFlute = 0;
          let totalBalanceBase = 0;
          let totalBalanceFlute = 0;
          
          materialTrackingData.forEach((row, idx) => {
            const usedWeight = parseFloat(row.usedWeight) || 0;
            const balanceWeight = parseFloat(row.balanceWeight) || 0;
            
            // Even indices are base reels, odd indices are flute reels (based on WO structure)
            if (idx % 2 === 0) {
              totalUsedBase += usedWeight;
              totalBalanceBase += balanceWeight;
            } else {
              totalUsedFlute += usedWeight;
              totalBalanceFlute += balanceWeight;
            }
          });
          
          await axios.patch(`${API}/work-orders/${woNum}/update-actual-counts`, {
            process: "process1",
            actual_used_base_weight: totalUsedBase,
            actual_used_flute_weight: totalUsedFlute,
            balance_base_weight: totalBalanceBase,
            balance_flute_weight: totalBalanceFlute,
            employee_name: activeEntry?.employee_name || selectedEmployee
          });
        } catch (error) {
          console.error("Error saving P1 material usage:", error);
        }
      }
      
      // Save production counts for various machines
      const woNum = pendingStopData?.workorderNumber || workorderNumber;
      const machineId = activeEntry?.machine_id;
      
      // M002A - Sheet Cutting (P2A) - only sheet count
      if (machineId === "M002A" && productionCounts.sheetCounting) {
        try {
          await axios.patch(`${API}/work-orders/${woNum}/update-actual-counts`, {
            process: "process2a",
            actual_sheet_counting: parseInt(productionCounts.sheetCounting) || 0,
            employee_name: activeEntry?.employee_name || selectedEmployee
          });
          toast.success("Sheet count saved!");
        } catch (error) {
          console.error("Error saving sheet count:", error);
        }
      }
      
      // M002B - Paper Cutting (P2B) - paper count + auto-calculated weight
      if (machineId === "M002B" && productionCounts.paperCounting) {
        try {
          await axios.patch(`${API}/work-orders/${woNum}/update-actual-counts`, {
            process: "process2b",
            actual_paper_counting: parseInt(productionCounts.paperCounting) || 0,
            employee_name: activeEntry?.employee_name || selectedEmployee
          });
          toast.success("Paper count saved!");
          
          // Auto-create bit reel OR mark reel as fully used
          if (p2bReelInfo && p2bCalc) {
            const balanceWeight = parseFloat(p2bCalc.balanceWeightKg) || 0;
            const usedWeight = parseFloat(p2bCalc.usedWeightKg) || 0;
            try {
              const inventoryResponse = await axios.get(`${API}/inventory`);
              const originalReel = inventoryResponse.data.find(item => item.reel_id === p2bReelInfo.reelId);
              
              if (originalReel) {
                if (balanceWeight > 0) {
                  // Create bit reel with balance weight
                  await axios.post(`${API}/inventory/bit-reel`, {
                    original_item_id: originalReel.id,
                    used_weight: usedWeight,
                    balance_weight: balanceWeight,
                    used_comment: `Paper cutting in ${woNum} - ${productionCounts.paperCounting} papers (auto-calculated)`,
                    work_order_number: woNum,
                    reel_type: null
                  });
                  toast.success(`Bit reel created: ${balanceWeight} kg (${p2bCalc.balancePapers} papers remaining)`);
                } else {
                  // Fully consumed — mark reel as used directly
                  await axios.post(`${API}/inventory/use?reel_id=${p2bReelInfo.reelId}&work_order_number=${woNum}`);
                  toast.success("Reel fully consumed and marked as used");
                }
              }
            } catch (err) {
              console.error("Error processing reel stock:", err);
              toast.error("Failed to update reel stock");
            }
          } else if (p2bReelInfo) {
            // Fallback: if calculation failed but we have reel info, still mark as used
            try {
              await axios.post(`${API}/inventory/use?reel_id=${p2bReelInfo.reelId}&work_order_number=${woNum}`);
              toast.info("Reel marked as used (auto-calculation unavailable)");
            } catch (err) {
              console.error("Fallback reel marking failed:", err);
            }
          }
        } catch (error) {
          console.error("Error saving paper count:", error);
        }
      }
      
      // M002 (Legacy) - Sheet Cutting (P2A) - only sheet count
      if (machineId === "M002" && m002SubProcess === "sheet_cutting" && productionCounts.sheetCounting) {
        try {
          await axios.patch(`${API}/work-orders/${woNum}/update-actual-counts`, {
            process: "process2a",
            actual_sheet_counting: parseInt(productionCounts.sheetCounting) || 0,
            employee_name: activeEntry?.employee_name || selectedEmployee
          });
          toast.success("Sheet count saved!");
        } catch (error) {
          console.error("Error saving sheet count:", error);
        }
      }
      
      // M002 (Legacy) - Paper Cutting (P2B) - paper count + auto-calculated weight
      if (machineId === "M002" && m002SubProcess === "paper_cutting" && productionCounts.paperCounting) {
        try {
          await axios.patch(`${API}/work-orders/${woNum}/update-actual-counts`, {
            process: "process2b",
            actual_paper_counting: parseInt(productionCounts.paperCounting) || 0,
            employee_name: activeEntry?.employee_name || selectedEmployee
          });
          toast.success("Paper count saved!");
          
          // Auto-create bit reel OR mark reel as fully used
          if (p2bReelInfo && p2bCalc) {
            const balanceWeight = parseFloat(p2bCalc.balanceWeightKg) || 0;
            const usedWeight = parseFloat(p2bCalc.usedWeightKg) || 0;
            try {
              const inventoryResponse = await axios.get(`${API}/inventory`);
              const originalReel = inventoryResponse.data.find(item => item.reel_id === p2bReelInfo.reelId);
              
              if (originalReel) {
                if (balanceWeight > 0) {
                  await axios.post(`${API}/inventory/bit-reel`, {
                    original_item_id: originalReel.id,
                    used_weight: usedWeight,
                    balance_weight: balanceWeight,
                    used_comment: `Paper cutting in ${woNum} - ${productionCounts.paperCounting} papers (auto-calculated)`,
                    work_order_number: woNum,
                    reel_type: null
                  });
                  toast.success(`Bit reel created: ${balanceWeight} kg (${p2bCalc.balancePapers} papers remaining)`);
                } else {
                  await axios.post(`${API}/inventory/use?reel_id=${p2bReelInfo.reelId}&work_order_number=${woNum}`);
                  toast.success("Reel fully consumed and marked as used");
                }
              }
            } catch (err) {
              console.error("Error processing reel stock:", err);
              toast.error("Failed to update reel stock");
            }
          } else if (p2bReelInfo) {
            try {
              await axios.post(`${API}/inventory/use?reel_id=${p2bReelInfo.reelId}&work_order_number=${woNum}`);
              toast.info("Reel marked as used (auto-calculation unavailable)");
            } catch (err) {
              console.error("Fallback reel marking failed:", err);
            }
          }
        } catch (error) {
          console.error("Error saving paper count:", error);
        }
      }
      
      // M003 - Sheet Pasting board counting
      if (machineId === "M003" && productionCounts.boardCountingP3) {
        try {
          await axios.patch(`${API}/work-orders/${woNum}/update-actual-counts`, {
            process: "process3",
            actual_board_counting: parseInt(productionCounts.boardCountingP3) || 0,
            employee_name: activeEntry?.employee_name || selectedEmployee
          });
          toast.success("Board count saved!");
        } catch (error) {
          console.error("Error saving board count:", error);
        }
      }
      
      // M004 - Thin Blade Slitter board counting
      if (machineId === "M004" && productionCounts.boardCountingP4) {
        try {
          await axios.patch(`${API}/work-orders/${woNum}/update-actual-counts`, {
            process: "process4",
            actual_board_counting: parseInt(productionCounts.boardCountingP4) || 0,
            employee_name: activeEntry?.employee_name || selectedEmployee
          });
          toast.success("Board count saved!");
        } catch (error) {
          console.error("Error saving board count:", error);
        }
      }
      
      // M005 - 4 Bar Rotary board counting
      if (machineId === "M005" && productionCounts.boardCountingP5) {
        try {
          await axios.patch(`${API}/work-orders/${woNum}/update-actual-counts`, {
            process: "process5",
            actual_board_counting: parseInt(productionCounts.boardCountingP5) || 0,
            employee_name: activeEntry?.employee_name || selectedEmployee
          });
          toast.success("Board count saved!");
        } catch (error) {
          console.error("Error saving board count:", error);
        }
      }
      
      // M006 - RS4 Printing board counting
      if (machineId === "M006" && productionCounts.boardCountingP6) {
        try {
          await axios.patch(`${API}/work-orders/${woNum}/update-actual-counts`, {
            process: "process6",
            actual_board_counting: parseInt(productionCounts.boardCountingP6) || 0,
            employee_name: activeEntry?.employee_name || selectedEmployee
          });
          toast.success("Board count saved!");
        } catch (error) {
          console.error("Error saving board count:", error);
        }
      }
      
      // M007 - Flat Bed Punching board counting
      if (machineId === "M007" && productionCounts.boardCountingP7) {
        try {
          await axios.patch(`${API}/work-orders/${woNum}/update-actual-counts`, {
            process: "process7",
            actual_board_counting: parseInt(productionCounts.boardCountingP7) || 0,
            employee_name: activeEntry?.employee_name || selectedEmployee
          });
          toast.success("Board count saved!");
        } catch (error) {
          console.error("Error saving board count:", error);
        }
      }
      
      // M008 - Rotary Die Punching board counting
      if (machineId === "M008" && productionCounts.boardCountingP8) {
        try {
          await axios.patch(`${API}/work-orders/${woNum}/update-actual-counts`, {
            process: "process8",
            actual_board_counting: parseInt(productionCounts.boardCountingP8) || 0,
            employee_name: activeEntry?.employee_name || selectedEmployee
          });
          toast.success("Board count saved!");
        } catch (error) {
          console.error("Error saving board count:", error);
        }
      }
      
      // M010 - Stitching & Bundling total boxes
      if (machineId === "M010" && productionCounts.totalBoxesP10) {
        try {
          await axios.patch(`${API}/work-orders/${woNum}/update-actual-counts`, {
            process: "process10",
            actual_total_boxes: parseInt(productionCounts.totalBoxesP10) || 0,
            employee_name: activeEntry?.employee_name || selectedEmployee
          });
          toast.success("Total boxes count saved!");
        } catch (error) {
          console.error("Error saving total boxes count:", error);
        }
      }
      
      // M009 - Calico Pasting board counting
      if (machineId === "M009" && productionCounts.boardCountingP9) {
        try {
          await axios.patch(`${API}/work-orders/${woNum}/update-actual-counts`, {
            process: "process9",
            actual_board_counting: parseInt(productionCounts.boardCountingP9) || 0,
            employee_name: activeEntry?.employee_name || selectedEmployee
          });
          toast.success("Board count saved!");
        } catch (error) {
          console.error("Error saving board count:", error);
        }
      }
      
      // M011 - Loading / Unloading count
      if (machineId === "M011" && productionCounts.loadingCountP11) {
        try {
          await axios.patch(`${API}/work-orders/${woNum}/update-actual-counts`, {
            process: "process11",
            actual_loading_count: parseInt(productionCounts.loadingCountP11) || 0,
            employee_name: activeEntry?.employee_name || selectedEmployee
          });
          toast.success("Loading count saved!");
        } catch (error) {
          console.error("Error saving loading count:", error);
        }
      }
      
      // M012 - Transport count
      if (machineId === "M012" && productionCounts.transportCountP12) {
        try {
          await axios.patch(`${API}/work-orders/${woNum}/update-actual-counts`, {
            process: "process12",
            actual_transport_count: parseInt(productionCounts.transportCountP12) || 0,
            employee_name: activeEntry?.employee_name || selectedEmployee
          });
          toast.success("Transport count saved!");
        } catch (error) {
          console.error("Error saving transport count:", error);
        }
      }
      
      // Now proceed with the actual stop
      await finalizeStop();
      
    } catch (error) {
      console.error("Error submitting material tracking:", error);
      toast.error("Failed to save material tracking data");
    } finally {
      setLoading(false);
      setShowMaterialTrackingDialog(false);
      setMaterialTrackingData([]);
      setPendingStopData(null);
      setCorrugatedRollsCreated(""); // Reset corrugated rolls
      setProductionCounts({ // Reset production counts
        sheetCounting: "",
        paperCounting: "",
        balanceReelWeight: "",
        boardCountingP3: "",
        boardCountingP4: "",
        boardCountingP5: "",
        boardCountingP6: "",
        boardCountingP7: "",
        boardCountingP8: "",
        boardCountingP9: "",
        totalBoxesP10: "",
        loadingCountP11: "",
        transportCountP12: ""
      });
      setM002SubProcess("sheet_cutting"); // Reset sub-process selection
    }
  };

  // Finalize the stop after material tracking
  const finalizeStop = async () => {
    if (!pendingStopData) return;
    
    const { currentTime, workOrderStatusValue } = pendingStopData;
    
    await axios.put(`${API}/timesheet/${activeEntry.id}`, {
      end_time: currentTime,
      status: "completed",
      work_order_status: workOrderStatusValue,
    });

    // If process is selected and work order status is completed, mark process as completed
    if (selectedProcess && workOrderStatusValue === "completed" && workorderNumber) {
      try {
        await axios.patch(`${API}/work-orders/${workorderNumber}/complete-process`, null, {
          params: {
            process_name: selectedProcess,
            employee_id: activeEntry.employee_id,
            employee_name: activeEntry.employee_name
          }
        });
      } catch (error) {
        console.error("Error marking process as completed:", error);
      }
    }

    toast.success("Work stopped successfully!");
    
    // Reset states
    setActiveEntry(null);
    setLocalClockInTime(null);
    setSelectedEmployee("");
    setSelectedMachine("");
    setWorkorderNumber("");
    setElapsedTime(0);
    setCurrentDeadline(null);
    setWorkOrderStatus("in_progress");
    setProcessAlreadyCompleted(false);
    
    fetchActiveEmployees();
    localStorage.removeItem('selectedEmployee');
  };

  const checkActiveEntry = async (employeeName, preserveLocalTime = false) => {
    try {
      const response = await axios.get(`${API}/timesheet/active/${employeeName}`);
      if (response.data) {
        // Set active entry data
        setActiveEntry(response.data);
        setSelectedMachine(response.data.machine_id);
        setWorkorderNumber(response.data.workorder_number);
        
        // Only clear localClockInTime if not preserving (e.g., on manual refresh)
        // For WebSocket updates, we want to keep the localClockInTime that was just set
        if (!preserveLocalTime) {
          // For existing sessions loaded on page load/refresh, calculate from server time
          const startTimeParts = response.data.start_time.split(':');
          // Construct proper time string
          const timeString = startTimeParts.length === 3 
            ? response.data.start_time  // Already has seconds (HH:MM:SS)
            : `${response.data.start_time}:00`;  // Add seconds (HH:MM -> HH:MM:00)
          const serverClockInTime = new Date(`${response.data.date}T${timeString}Z`);
          setLocalClockInTime(serverClockInTime);
        }
      } else {
        // No active entry, clear everything
        setActiveEntry(null);
        setElapsedTime(0);
        setLocalClockInTime(null);
      }
    } catch (error) {
      console.error("Error checking active entry:", error);
    }
  };

  const handleEmployeeChange = (value) => {
    if (value !== "placeholder") {
      setSelectedEmployee(value);
      // Save to localStorage for page refresh persistence
      localStorage.setItem('selectedEmployee', value);
      checkActiveEntry(value);
    }
  };

  const findDeadline = (workOrderNum, machineId) => {
    const workOrder = workOrders.find(wo => wo.work_order_number === workOrderNum);
    if (workOrder && workOrder.machine_deadlines) {
      const machineDeadline = workOrder.machine_deadlines.find(md => md.machine_id === machineId);
      return machineDeadline?.deadline || null;
    }
    return null;
  };

  const handleStart = async () => {
    if (!selectedEmployee || selectedEmployee === "placeholder") {
      toast.error("Please select an employee");
      return;
    }
    if (!selectedMachine || selectedMachine === "placeholder") {
      toast.error("Please select a machine");
      return;
    }
    // Work order is optional ONLY when the machine is "OTHER" (general / non-production work)
    const isOtherMachine = selectedMachine === "OTHER";
    if (!isOtherMachine && !workorderNumber.trim()) {
      toast.error("Please enter a work order number");
      return;
    }

    // Check if this employee already has an active session
    if (activeEntry) {
      toast.error("You already have an active session. Please stop it first.");
      return;
    }

    try {
      setLoading(true);
      const now = new Date();
      const currentDate = now.toISOString().split("T")[0]; // UTC date
      const currentTime = `${String(now.getUTCHours()).padStart(2, "0")}:${String(now.getUTCMinutes()).padStart(2, "0")}:${String(now.getUTCSeconds()).padStart(2, "0")}`; // UTC time with seconds

      const machine = machines.find((m) => m.id === selectedMachine);
      if (!machine) {
        toast.error("Machine not found");
        return;
      }

      const response = await axios.post(`${API}/timesheet`, {
        employee_name: selectedEmployee,
        machine_id: selectedMachine,
        machine_name: machine.name,
        workorder_number: workorderNumber,
        date: currentDate,
        start_time: currentTime,
        status: "ongoing",
      });

      // Record the EXACT moment of clock-in locally for accurate timer
      const clockInMoment = new Date();
      setLocalClockInTime(clockInMoment);
      
      // Set elapsed time to 0 and then set active entry
      setElapsedTime(0);
      setActiveEntry(response.data);
      
      // Find and set deadline
      const deadline = findDeadline(workorderNumber, selectedMachine);
      setCurrentDeadline(deadline);
      
      fetchActiveEmployees(); // Refresh active employees list
      toast.success(`${selectedEmployee} clocked in successfully!`);
      
      // Auto-clear form after 2 seconds to allow next employee to clock in
      setTimeout(() => {
        setActiveEntry(null);
        setLocalClockInTime(null);
        setSelectedEmployee("");
        setSelectedMachine("");
        setWorkorderNumber("");
        setElapsedTime(0);
        setCurrentDeadline(null);
        localStorage.removeItem('selectedEmployee');
        toast.info("Ready for next employee");
      }, 2000);
      
    } catch (error) {
      console.error("Error starting work:", error);
      toast.error("Failed to start work");
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    if (!activeEntry) {
      toast.error("No active work session");
      return;
    }

    // Check if this process/machine is already completed for this work order
    try {
      const response = await axios.get(`${API}/timesheet/check-process-completed`, {
        params: {
          workorder_number: activeEntry.workorder_number,
          machine_id: activeEntry.machine_id
        }
      });
      
      if (response.data.is_completed) {
        // Process already completed by someone else - directly stop without dialog
        toast.info(`Work order already marked completed by ${response.data.completed_by}. Stopping your session.`);
        await confirmStopDirect("in_progress");
        return;
      }
    } catch (error) {
      console.error("Error checking process completion:", error);
      // Continue with normal flow if check fails
    }

    // Normal flow - show status dialog with all options
    setProcessAlreadyCompleted(false);
    setShowStatusDialog(true);
  };

  // Direct stop without dialog (for already completed processes)
  const confirmStopDirect = async (status) => {
    setLoading(true);
    try {
      const now = new Date();
      const currentTime = `${String(now.getUTCHours()).padStart(2, "0")}:${String(now.getUTCMinutes()).padStart(2, "0")}:${String(now.getUTCSeconds()).padStart(2, "0")}`;

      await axios.put(`${API}/timesheet/${activeEntry.id}`, {
        end_time: currentTime,
        status: "completed",
        work_order_status: status,
      });

      toast.success("Work stopped successfully!");
      
      // Reset states
      setActiveEntry(null);
      setLocalClockInTime(null);
      setSelectedEmployee("");
      setSelectedMachine("");
      setWorkorderNumber("");
      setElapsedTime(0);
      setCurrentDeadline(null);
      setWorkOrderStatus("in_progress");
      setProcessAlreadyCompleted(false);
      
      fetchActiveEmployees();
      localStorage.removeItem('selectedEmployee');
    } catch (error) {
      console.error("Error stopping work:", error);
      toast.error("Failed to stop work");
    } finally {
      setLoading(false);
    }
  };

  const handleProcessSelected = () => {
    // After process is selected, show status dialog
    setShowProcessDialog(false);
    setShowStatusDialog(true);
  };

  const refreshAfterStop = () => {
    setActiveEntry(null);
    setLocalClockInTime(null);
    setSelectedEmployee("");
    setSelectedMachine("");
    setWorkorderNumber("");
    setElapsedTime(0);
    setCurrentDeadline(null);
    setWorkOrderStatus("in_progress");
    setProcessAlreadyCompleted(false);
    fetchActiveEmployees();
    localStorage.removeItem('selectedEmployee');
  };

  const confirmStop = async () => {
    const now = new Date();
    const currentTime = `${String(now.getUTCHours()).padStart(2, "0")}:${String(now.getUTCMinutes()).padStart(2, "0")}:${String(now.getUTCSeconds()).padStart(2, "0")}`;
    
    // Check if this is a machine that requires material/production tracking dialog on completion
    // M001: Corrugation, M002: Sheet Cutting, M003: Sheet Pasting (material tracking)
    // M004-M008, M010: Production counting only
    const machineId = activeEntry?.machine_id;
    const requiresMaterialTracking = 
      (machineId === "M001" || machineId === "M002" || machineId === "M002A" || machineId === "M002B" || machineId === "M003") && 
      workOrderStatus === "completed";
    
    const requiresProductionCounting = 
      (machineId === "M004" || machineId === "M005" || machineId === "M006" || 
       machineId === "M007" || machineId === "M008" ||
       machineId === "M009A" || machineId === "M010") && 
      workOrderStatus === "completed";
    
    if (requiresMaterialTracking) {
      // Store pending stop data and show material tracking dialog
      setPendingStopData({
        currentTime,
        workOrderStatusValue: workOrderStatus,
        workorderNumber: activeEntry?.workorder_number || workorderNumber
      });
      
      const woNum = activeEntry?.workorder_number || workorderNumber;
      
      // Fetch reels for this work order
      const { reels: matchedReels, reelIds: woReelIds, reelMeta } = await fetchWorkOrderReels(woNum, activeEntry?.machine_id);
      await fetchAvailableReels();
      
      // Fetch already-tracked reels for cross-machine flow
      let alreadyTracked = [];
      try {
        const trackedRes = await axios.get(`${API}/work-orders/${woNum}/material-tracked-reels`);
        alreadyTracked = trackedRes.data.material_tracked_reels || [];
      } catch (e) {
        // ignore - no tracked reels yet
      }
      
      // Filter out already-tracked reels (using reelMeta for labels)
      const untrackedMeta = reelMeta.filter(meta => {
        const reel = matchedReels.find(r => r.reel_id === meta.id || r.rm_name === meta.id);
        const reelId = reel ? reel.reel_id : meta.id;
        return !alreadyTracked.includes(reelId);
      });
      
      // If all reels already tracked, skip material dialog entirely
      if (woReelIds.length > 0 && untrackedMeta.length === 0) {
        // All reels already tracked - skip dialog and proceed
        await axios.put(`${API}/timesheet/${activeEntry.id}`, {
          end_time: currentTime,
          status: "completed",
          work_order_status: workOrderStatus,
        });
        if (selectedProcess && workorderNumber) {
          try {
            await axios.patch(`${API}/work-orders/${workorderNumber}/complete-process`, null, {
              params: { process_name: selectedProcess, employee_name: selectedEmployee }
            });
          } catch (e) {}
        }
        toast.success("All material already tracked. Work completed!");
        setShowStatusDialog(false);
        setPendingStopData(null);
        await refreshAfterStop();
        return;
      }
      
      // Pre-populate rows based on untracked reels with labels
      // All materials are now shown as read-only (assigned from work order)
      let initialRows = [];
      if (untrackedMeta.length > 0) {
        initialRows = untrackedMeta.map((meta, idx) => {
          const reel = matchedReels.find(r => r.reel_id === meta.id || r.rm_name === meta.id);
          const isCorrugatedRoll = reel?.product === 'Corrugated Roll';
          return {
            id: Date.now() + idx,
            reelId: reel ? reel.reel_id : meta.id,
            label: meta.label,
            source: meta.source, // corrugation, sheet_cutting, or sheet_pasting
            reelType: meta.reelType || null, // "base" or "flute" for RM Name suffix
            usedWeight: "",
            balanceWeight: "",
            reelData: reel || null,
            isPrintedPaper: meta.isPrintedPaper || false, // For Process 3 printed papers
            isCorrugatedRoll: isCorrugatedRoll || false, // Track if this is a corrugated roll
            isAssigned: true, // All materials are pre-assigned from work order (read-only)
            assignedWeight: meta.weight || reel?.reel_weight || "", // Weight assigned in work order
            assignedQty: meta.qty || reel?.qty || "", // Qty assigned in work order
            usedQty: "", // Qty used
            balanceQty: "" // Balance qty
          };
        });
      }
      
      // Fallback: at least one empty row if no reels found
      // But NOT for M002 - for M002, we only show counting inputs, not reel selection
      if (initialRows.length === 0 && activeEntry?.machine_id !== "M002" && activeEntry?.machine_id !== "M002A" && activeEntry?.machine_id !== "M002B") {
        initialRows = [{ id: Date.now(), reelId: "", usedWeight: "", balanceWeight: "", reelData: null, isAssigned: false }];
      }
      
      setMaterialTrackingData(initialRows);
      
      // For M002B: Clear material tracking rows since P2B uses auto-calculation, not manual entry
      if (activeEntry?.machine_id === "M002B") {
        setMaterialTrackingData([]);
      }
      
      // For M002B or M002: fetch P2B reel info for auto-calculation in material dialog
      setP2bReelInfo(null);
      if (activeEntry?.machine_id === "M002B" || activeEntry?.machine_id === "M002") {
        try {
          const p2bWoNum = activeEntry?.workorder_number || workorderNumber;
          const woResponse = await axios.get(`${API}/work-orders/by-number/${p2bWoNum}`);
          const wo = woResponse.data;
          const p2b = wo.processes?.process2b || {};
          const header = wo.header || {};
          const reelSize = parseFloat(p2b.reels?.[0]?.reel_size || header.reel_size) || 0;
          const cuttingSize = parseFloat(p2b.cutting_size || header.cutting_size) || 0;
          const reelNo = p2b.reels?.[0]?.reel_no || '';
          
          if (reelNo) {
            const invResponse = await axios.get(`${API}/inventory`);
            const invItem = invResponse.data.find(item => item.reel_id === reelNo);
            if (invItem) {
              const gsm = invItem.page_no ? parseFloat(invItem.page_no) : extractGSMFromRmName(invItem.rm_name);
              const originalWeight = parseFloat(invItem.reel_weight) || 0;
              setP2bReelInfo({ gsm, reelSize, cuttingSize, originalWeight, reelId: reelNo });
            }
          }
        } catch (err) {
          console.error("Error fetching P2B reel info:", err);
        }
      }
      
      setShowStatusDialog(false);
      setShowMaterialTrackingDialog(true);
      return;
    }
    
    // For machines that require production counting (M004-M008, M010)
    if (requiresProductionCounting) {
      // Store pending stop data and show production counting dialog
      setPendingStopData({
        currentTime,
        workOrderStatusValue: workOrderStatus,
        workorderNumber: activeEntry?.workorder_number || workorderNumber
      });
      
      // Fetch reels for M002 to check if any are assigned
      let m002HasReels = false;
      if (activeEntry?.machine_id === "M002") {
        const woNum = activeEntry?.workorder_number || workorderNumber;
        const { reelMeta } = await fetchWorkOrderReels(woNum, "M002");
        m002HasReels = reelMeta && reelMeta.length > 0 && reelMeta.some(m => m.source === "sheet_cutting" || m.source === "paper_cutting");
      }
      
      // Reset production counts
      setProductionCounts({
        sheetCounting: "",
        paperCounting: "",
        balanceReelWeight: "",
        boardCountingP3: "",
        boardCountingP4: "",
        boardCountingP5: "",
        boardCountingP6: "",
        boardCountingP7: "",
        boardCountingP8: "",
        boardCountingP9: "",
        totalBoxesP10: "",
        loadingCountP11: "",
        transportCountP12: ""
      });
      setM002SubProcess("sheet_cutting"); // Reset sub-process selection
      setP2bReelInfo(null); // Reset P2B reel info
      
      // Fetch P2B reel info for auto-calculation (M002B or M002 paper cutting)
      if (activeEntry?.machine_id === "M002B" || activeEntry?.machine_id === "M002") {
        try {
          const woNum = activeEntry?.workorder_number || workorderNumber;
          const woResponse = await axios.get(`${API}/work-orders/by-number/${woNum}`);
          const wo = woResponse.data;
          const p2b = wo.processes?.process2b || {};
          const header = wo.header || {};
          const reelSize = parseFloat(p2b.reels?.[0]?.reel_size || header.reel_size) || 0;
          const cuttingSize = parseFloat(p2b.cutting_size || header.cutting_size) || 0;
          const reelNo = p2b.reels?.[0]?.reel_no || '';
          
          if (reelNo) {
            const invResponse = await axios.get(`${API}/inventory`);
            const invItem = invResponse.data.find(item => item.reel_id === reelNo);
            if (invItem) {
              const gsm = invItem.page_no ? parseFloat(invItem.page_no) : extractGSMFromRmName(invItem.rm_name);
              const originalWeight = parseFloat(invItem.reel_weight) || 0;
              setP2bReelInfo({ gsm, reelSize, cuttingSize, originalWeight, reelId: reelNo });
            }
          }
        } catch (err) {
          console.error("Error fetching P2B reel info:", err);
        }
      }
      
      // For M002: Only set material tracking data if reels are assigned
      if (activeEntry?.machine_id === "M002" && m002HasReels) {
        const woNum = activeEntry?.workorder_number || workorderNumber;
        const { reels: matchedReels, reelMeta } = await fetchWorkOrderReels(woNum, "M002");
        
        const initialRows = reelMeta.map((meta, idx) => {
          const reel = matchedReels.find(r => r.reel_id === meta.id || r.rm_name === meta.id);
          return {
            id: Date.now() + idx,
            reelId: reel ? reel.reel_id : meta.id,
            label: meta.label,
            source: meta.source,
            usedWeight: "",
            balanceWeight: "",
            reelData: reel || null,
            isAssigned: true,
            assignedWeight: meta.weight || reel?.reel_weight || ""
          };
        });
        setMaterialTrackingData(initialRows);
      } else {
        // Set empty material tracking data (no material tracking needed, only counts)
        setMaterialTrackingData([]);
      }
      
      setShowStatusDialog(false);
      setShowMaterialTrackingDialog(true);
      return;
    }
    
    // For other machines or non-completed status, proceed directly
    setLoading(true);
    try {
      await axios.put(`${API}/timesheet/${activeEntry.id}`, {
        end_time: currentTime,
        status: "completed",
        work_order_status: workOrderStatus,
      });

      // If process is selected and work order status is completed, mark process as completed
      if (selectedProcess && workOrderStatus === "completed" && workorderNumber) {
        try {
          await axios.patch(`${API}/work-orders/${workorderNumber}/complete-process`, null, {
            params: {
              process_name: selectedProcess,
              employee_id: activeEntry.employee_id,
              employee_name: activeEntry.employee_name
            }
          });
          toast.success(`Process ${selectedProcess} marked as completed!`);
        } catch (error) {
          console.error("Error marking process as completed:", error);
        }
      }

      // If work order status is "completed", clock out all other employees on same work order + machine
      // BUT mark their status as "in_progress" (not completed) since they didn't complete it
      if (workOrderStatus === "completed" && workorderNumber && activeEntry.machine_id) {
        try {
          // Find all active employees working on the same work order and machine
          const otherActiveEmployees = activeEmployees.filter(emp => 
            emp.workorder_number === workorderNumber && 
            emp.machine_id === activeEntry.machine_id &&
            emp.id !== activeEntry.id // Exclude the current employee (already clocked out)
          );

          // Clock out each employee with "in_progress" status (not completed)
          for (const emp of otherActiveEmployees) {
            try {
              await axios.put(`${API}/timesheet/${emp.id}`, {
                end_time: currentTime,
                status: "completed",
                work_order_status: "in_progress", // Changed from "completed" to "in_progress"
              });
            } catch (error) {
              console.error(`Failed to clock out ${emp.employee_name}:`, error);
            }
          }

          if (otherActiveEmployees.length > 0) {
            toast.success(`Clocked out ${otherActiveEmployees.length} other employee(s) on this work order + machine`);
          }
        } catch (error) {
          console.error("Error auto-clocking out other employees:", error);
        }
      }

      toast.success("Work completed successfully!");
      
      // Clear localStorage when clocking out
      localStorage.removeItem('selectedEmployee');
      
      // Reset form
      setActiveEntry(null);
      setLocalClockInTime(null); // Clear local clock-in time
      setCurrentDeadline(null);
      setSelectedEmployee("");
      setSelectedMachine("");
      setWorkorderNumber("");
      setElapsedTime(0);
      setWorkOrderStatus("in_progress");
      setSelectedProcess("");
      setShowStatusDialog(false);
      setShowProcessDialog(false);
      fetchActiveEmployees(); // Refresh active employees list
    } catch (error) {
      console.error("Error stopping work:", error);
      toast.error("Failed to stop work");
    } finally {
      setLoading(false);
    }
  };

  const formatElapsedTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8" style={{ background: "radial-gradient(ellipse at center, #0A1910 0%, #000000 70%)" }}>
      <div className="max-w-6xl mx-auto">
        {/* Logo - clickable to home */}
        <div className="flex justify-center mb-6">
          <Link to="/">
            <Logo size="lg" showText={true} />
          </Link>
        </div>
        
        {/* Header - Responsive Layout */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-4">
          <div className="text-center sm:text-left">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-blue-400 mb-1 sm:mb-2">
              {t.employeeTimesheet}
            </h1>
            <p className="text-sm sm:text-lg text-blue-200">{activeEntry ? t.workingOn : t.selectEmployee}</p>
          </div>
          <div className="flex gap-2 sm:gap-3 justify-center sm:justify-end items-center">
            {/* Theme Toggle */}
            <ThemeToggle />
            
            {/* Language Selector */}
            <Select value={language} onValueChange={(val) => {
              setLanguage(val);
              localStorage.setItem('employeeLanguage', val);
            }}>
              <SelectTrigger className="w-[120px] bg-white border-2 border-blue-400 text-black font-semibold">
                <Globe className="h-4 w-4 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(languageNames).map(([code, name]) => (
                  <SelectItem key={code} value={code}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Link to="/">
              <Button 
                variant="outline" 
                size="default"
                className="bg-white hover:bg-blue-50 border-2 border-blue-400 text-black font-semibold text-sm sm:text-lg px-3 sm:px-6 py-2 sm:py-3 shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <ArrowLeft className="h-5 w-5 sm:h-6 sm:w-6 sm:mr-2" />
                <span className="hidden sm:inline">Home</span>
              </Button>
            </Link>
          </div>
        </div>

        <div className={`grid grid-cols-1 ${(isMobile || isPWA) ? '' : 'lg:grid-cols-3'} gap-4 sm:gap-6`}>
          {/* Main Card - 2 columns on desktop, full width on mobile */}
          <div className={`${(isMobile || isPWA) ? '' : 'lg:col-span-2'}`}>
            <Card className="bg-white shadow-2xl border-none">
              <CardHeader className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-t-lg py-3 sm:py-4">
                <CardTitle className="text-xl sm:text-2xl font-bold text-center sm:text-left">
                  {activeEntry ? t.workingOn : t.selectEmployee}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-8">
                {/* Active Timer */}
                {activeEntry && (
                  <div className="space-y-4 mb-8">
                    {/* Employee Session Timer */}
                    <div className="p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-300">
                      <div className="flex items-center justify-center mb-4">
                        <Clock className="h-8 w-8 text-green-600 mr-3 animate-pulse" />
                        <span className="text-sm font-medium text-gray-700">
                          Active Session
                        </span>
                      </div>
                      <div className="text-center">
                        <div className="text-6xl font-bold text-gray-800 mb-2">
                          {formatElapsedTime(elapsedTime)}
                        </div>
                        <p className="text-gray-600">
                          Started at {formatTimeForDisplay(activeEntry.date, activeEntry.start_time)}
                        </p>
                      </div>
                    </div>

                    {/* Deadline */}
                    {currentDeadline && (
                      <div className="p-3 bg-blue-100 border-2 border-blue-400 rounded-lg">
                        <p className="text-sm font-semibold text-yellow-800 text-center">
                          📅 Deadline for this machine
                        </p>
                        <p className="text-lg font-bold text-yellow-900 text-center">
                          {new Date(currentDeadline).toLocaleDateString('en-IN', { 
                            day: 'numeric', 
                            month: 'long', 
                            year: 'numeric' 
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Form */}
                <div className="space-y-6">
                  {/* Employee Selection */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="employee" className="text-lg font-semibold">
                        {t.selectEmployee} *
                      </Label>
                      {activeEntry && (
                        <Button
                          type="button"
                          variant="outline"
                          size="default"
                          onClick={() => {
                            // Clear selection to allow selecting another employee
                            setActiveEntry(null);
                            setLocalClockInTime(null);
                            setSelectedEmployee("");
                            setSelectedMachine("");
                            setWorkorderNumber("");
                            setElapsedTime(0);
                            setCurrentDeadline(null);
                            localStorage.removeItem('selectedEmployee');
                            toast.info("Selection cleared. You can now select another employee.");
                          }}
                          className="text-sm font-semibold px-4 py-2"
                        >
                          Switch Employee
                        </Button>
                      )}
                    </div>
                    <Select
                      value={selectedEmployee || "placeholder"}
                      onValueChange={handleEmployeeChange}
                      disabled={!!activeEntry}
                    >
                      <SelectTrigger
                        data-testid="employee-select"
                        className="h-14 text-lg border-2"
                      >
                        <SelectValue placeholder="Select your name" />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Search Input */}
                        <div className="px-2 pb-2 sticky top-0 bg-white z-10">
                          <input
                            type="text"
                            placeholder="Type to search..."
                            className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                              }
                            }}
                            onChange={(e) => {
                              const searchTerm = e.target.value.toLowerCase();
                              const items = document.querySelectorAll('[role="option"]');
                              items.forEach(item => {
                                const text = item.textContent.toLowerCase();
                                if (text.includes(searchTerm) || text === 'select your name') {
                                  item.style.display = '';
                                } else {
                                  item.style.display = 'none';
                                }
                              });
                            }}
                          />
                        </div>
                        <SelectItem value="placeholder" disabled>
                          Select your name
                        </SelectItem>
                        {employees
                          .filter(emp => emp && emp.name && (emp.status === "Employed" || emp.status === "Currently Employed" || !emp.status))
                          .map((emp) => (
                          <SelectItem key={emp.id} value={emp.name}>
                            {emp.name} - {emp.category || "CPI"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Work Order - MOVED ABOVE MACHINE */}
                  <div className="space-y-2">
                    <Label htmlFor="workorder" className="text-lg font-semibold">
                      Work Order Number {selectedMachine === "OTHER" ? <span className="text-sm text-gray-400 font-normal">(optional for OTHER)</span> : "*"}
                    </Label>
                    <Select
                      value={workorderNumber || "placeholder"}
                      onValueChange={handleWorkOrderChange}
                      disabled={!!activeEntry}
                    >
                      <SelectTrigger
                        data-testid="workorder-select"
                        className="h-14 text-lg border-2"
                      >
                        <SelectValue placeholder={t.selectWorkOrder} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="placeholder" disabled>
                          {t.selectWorkOrder}
                        </SelectItem>
                        {workOrders.map((wo) => (
                          <SelectItem key={wo.work_order_number} value={wo.work_order_number}>
                            {wo.work_order_number}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Machine Selection - NOW BELOW WORK ORDER */}
                  <div className="space-y-2">
                    <Label htmlFor="machine" className="text-lg font-semibold">
                      {t.selectMachine} *
                    </Label>
                    <Select
                      value={selectedMachine || "placeholder"}
                      onValueChange={(value) => {
                        if (value !== "placeholder") {
                          setSelectedMachine(value);
                        }
                      }}
                      disabled={!!activeEntry}
                    >
                      <SelectTrigger
                        data-testid="machine-select"
                        className="h-14 text-lg border-2"
                      >
                        <SelectValue placeholder={t.selectMachine} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="placeholder" disabled>
                          {t.selectMachine}
                        </SelectItem>
                        {/* When no work order is selected, show all machines */}
                        {/* When work order is selected, show filtered machines */}
                        {(workorderNumber ? (availableMachines.length > 0 ? availableMachines : machines) : machines).map((machine) => (
                          <SelectItem key={machine.id} value={machine.id}>
                            {machine.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Action Buttons */}
                  <div className="pt-4">
                    {!activeEntry ? (
                      <Button
                        data-testid="start-button"
                        onClick={handleStart}
                        disabled={loading}
                        className="w-full h-16 text-xl font-bold bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg"
                      >
                        <Play className="mr-3 h-6 w-6" />
                        {loading ? "..." : t.startWork}
                      </Button>
                    ) : (
                      <Button
                        data-testid="stop-button"
                        onClick={handleStop}
                        disabled={loading}
                        className="w-full h-16 text-xl font-bold bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-lg"
                      >
                        <Square className="mr-3 h-6 w-6" />
                        {loading ? "..." : t.stopWork}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Active Employees - 1 column - Hidden on Mobile/PWA */}
          {!(isMobile || isPWA) && (
            <div className="lg:col-span-1">
              <Card className="bg-white shadow-2xl border-none h-full">
                <CardHeader className="bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Users className="h-6 w-6 mr-2" />
                      <CardTitle className="text-xl font-bold">
                        {t.activeEmployees}
                      </CardTitle>
                    </div>
                    <span className="text-sm font-semibold bg-white/20 px-3 py-1 rounded-full">
                      {filteredActiveEmployees.length} Active
                    </span>
                  </div>
                </CardHeader>
              <CardContent className="p-6">
                {/* Machine Filter */}
                <div className="mb-4">
                  <Label htmlFor="machine-filter" className="text-sm font-semibold mb-2 block">
                    Filter by Machine
                  </Label>
                  <Select
                    value={machineFilter}
                    onValueChange={setMachineFilter}
                  >
                    <SelectTrigger className="h-10 text-sm border-2">
                      <SelectValue placeholder="All Machines" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Machines</SelectItem>
                      {machines.map((machine) => (
                        <SelectItem key={machine.id} value={machine.id}>
                          {machine.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Active Employees List */}
                {filteredActiveEmployees.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">
                      {machineFilter === "all" 
                        ? "No active sessions" 
                        : "No one working on this machine"}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredActiveEmployees.map((emp) => (
                      <div
                        key={emp.id}
                        className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200 relative"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-semibold text-gray-800">
                              {emp.employee_name}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                              {emp.machine_name}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              WO: {emp.workorder_number}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              Started: {emp.date} {formatTimeForDisplay(emp.date, emp.start_time)}
                            </p>
                            {/* Live Employee Timer */}
                            <LiveTimer 
                              startDate={emp.date} 
                              startTime={emp.start_time}
                              employeeName={emp.employee_name}
                              currentEmployee={selectedEmployee}
                              currentElapsedTime={elapsedTime}
                              currentLocalClockInTime={localClockInTime}
                            />
                          </div>
                          <div className="text-right flex flex-col items-end gap-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <Clock className="h-3 w-3 mr-1 animate-pulse" />
                              Active
                            </span>
                            {/* Force Stop Button - for stuck sessions */}
                            <button
                              onClick={async () => {
                                if (window.confirm(`Force stop session for ${emp.employee_name}?`)) {
                                  try {
                                    const now = new Date();
                                    const currentTime = `${String(now.getUTCHours()).padStart(2, "0")}:${String(now.getUTCMinutes()).padStart(2, "0")}:${String(now.getUTCSeconds()).padStart(2, "0")}`; // UTC time with seconds
                                    await axios.put(`${API}/timesheet/${emp.id}`, {
                                      end_time: currentTime,
                                      status: "completed"
                                    });
                                    toast.success("Session stopped successfully");
                                    fetchActiveEmployees();
                                  } catch (error) {
                                    toast.error("Failed to stop session");
                                  }
                                }
                              }}
                              className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded border border-red-300 transition-colors"
                            >
                              Force Stop
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          )}
        </div>

        {/* Available Employees (Not Working) Section - Hidden on Mobile/PWA */}
        {!(isMobile || isPWA) && (
        <div className="mt-6">
          <Card className="bg-white shadow-2xl border-none">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Users className="h-6 w-6 mr-2" />
                  <CardTitle className="text-xl font-bold">
                    Available Employees (Not Working)
                  </CardTitle>
                </div>
                <span className="text-sm font-semibold bg-white/20 px-3 py-1 rounded-full">
                  {employees.filter(emp => 
                    emp && emp.name && 
                    (emp.status === "Employed" || !emp.status) &&
                    !activeEmployees.some(active => active.employee_name === emp.name)
                  ).length} Available
                </span>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {employees.filter(emp => 
                emp && emp.name && 
                (emp.status === "Employed" || !emp.status) &&
                !activeEmployees.some(active => active.employee_name === emp.name)
              ).length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">All currently employed employees are working</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {employees
                    .filter(emp => emp && emp.name && 
                      (emp.status === "Employed" || !emp.status) &&
                      !activeEmployees.some(active => active.employee_name === emp.name))
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((emp) => (
                      <div
                        key={emp.id}
                        className="p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200"
                      >
                        <p className="font-semibold text-gray-800 text-sm">
                          {emp.name}
                        </p>
                        <span className="inline-flex items-center px-2 py-0.5 mt-2 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Available
                        </span>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        )}
      </div>

      {/* Process Selection Dialog - REMOVED */}

      {/* Inactivity Warning Overlay */}
      {showInactivityWarning && activeEntry && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4 bg-white">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl text-red-600">
                Session Timeout Warning
              </CardTitle>
              <div className={`text-5xl font-bold mt-4 ${
                inactivityCountdown <= 3 ? 'text-red-600 animate-pulse' : 'text-yellow-600'
              }`}>
                {inactivityCountdown}
              </div>
              <p className="text-gray-600 mt-4">
                No activity detected. Tap anywhere or click below to continue working.
              </p>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => {
                  setShowInactivityWarning(false);
                  setInactivityCountdown(10);
                }}
                className="w-full bg-green-600 hover:bg-green-700 text-white text-lg py-6"
              >
                Continue Working
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Status Selection Dialog */}
      {showStatusDialog && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4 bg-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-2xl text-gray-800">
                  Work Order Status
                </CardTitle>
                <div className={`text-2xl font-bold px-3 py-1 rounded-full ${
                  dialogCountdown <= 5 ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-yellow-100 text-yellow-600'
                }`}>
                  {dialogCountdown}s
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Please select the current status within {dialogCountdown} seconds.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <button
                  onClick={() => setWorkOrderStatus("completed")}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    workOrderStatus === "completed"
                      ? "border-green-500 bg-green-50"
                      : "border-gray-300 hover:border-green-300"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      workOrderStatus === "completed" ? "bg-green-500 border-green-500" : "border-gray-400"
                    }`} />
                    <div>
                      <div className="font-semibold text-gray-800">✅ Completed</div>
                      <div className="text-sm text-gray-600">Work order is finished</div>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setWorkOrderStatus("in_progress")}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    workOrderStatus === "in_progress"
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-300 hover:border-blue-300"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      workOrderStatus === "in_progress" ? "bg-blue-500 border-blue-500" : "border-gray-400"
                    }`} />
                    <div>
                      <div className="font-semibold text-gray-800">🔄 In Progress</div>
                      <div className="text-sm text-gray-600">Still working on it</div>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setWorkOrderStatus("on_hold")}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    workOrderStatus === "on_hold"
                      ? "border-yellow-500 bg-yellow-50"
                      : "border-gray-300 hover:border-yellow-300"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      workOrderStatus === "on_hold" ? "bg-yellow-500 border-yellow-500" : "border-gray-400"
                    }`} />
                    <div>
                      <div className="font-semibold text-gray-800">⏸️ On Hold</div>
                      <div className="text-sm text-gray-600">Temporarily paused</div>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setWorkOrderStatus("rejected")}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    workOrderStatus === "rejected"
                      ? "border-red-500 bg-red-50"
                      : "border-gray-300 hover:border-red-300"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      workOrderStatus === "rejected" ? "bg-red-500 border-red-500" : "border-gray-400"
                    }`} />
                    <div>
                      <div className="font-semibold text-gray-800">❌ Rejected</div>
                      <div className="text-sm text-gray-600">Work not accepted</div>
                    </div>
                  </div>
                </button>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={confirmStop}
                  disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {loading ? "Stopping..." : "Confirm & Stop"}
                </Button>
                <Button
                  onClick={() => setShowStatusDialog(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Material Tracking Dialog - Mobile-First Design */}
      {showMaterialTrackingDialog && (
        <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50">
          <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom duration-300">
            {/* Header - Sticky */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-white">Material Usage</h2>
                  <p className="text-emerald-100 text-xs sm:text-sm mt-1">
                    WO: {pendingStopData?.workorderNumber} • {activeEntry?.machine_name}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`text-sm font-bold px-2 py-1 rounded-full ${
                    materialDialogCountdown <= 15 ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-white/20 text-white'
                  }`}>
                    {Math.floor(materialDialogCountdown / 60)}:{String(materialDialogCountdown % 60).padStart(2, '0')}
                  </div>
                  <button 
                    onClick={() => {
                      setShowMaterialTrackingDialog(false);
                      setShowStatusDialog(true);
                      setPendingStopData(null);
                      setCorrugatedRollsCreated("");
                      setProductionCounts({
                        sheetCounting: "",
                        paperCounting: "",
                        balanceReelWeight: "",
                        boardCountingP3: "",
                        boardCountingP4: "",
                        boardCountingP5: "",
                        boardCountingP6: "",
                        boardCountingP7: "",
                        boardCountingP8: "",
                        totalBoxesP10: ""
                      });
                      setM002SubProcess("sheet_cutting");
                    }}
                    className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Process Type Badge */}
              <div className="flex justify-center">
                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                  {activeEntry?.machine_id === "M001" && "Corrugation Process"}
                  {activeEntry?.machine_id === "M002A" && "Sheet Cutting (P2A)"}
                  {activeEntry?.machine_id === "M002B" && "Paper Cutting (P2B)"}
                  {activeEntry?.machine_id === "M002" && m002SubProcess === "sheet_cutting" && "Sheet Cutting (P2A)"}
                  {activeEntry?.machine_id === "M002" && m002SubProcess === "paper_cutting" && "Paper Cutting (P2B)"}
                  {activeEntry?.machine_id === "M003" && "Sheet Pasting"}
                  {activeEntry?.machine_id === "M004" && "Thin Blade Slitter Scorer"}
                  {activeEntry?.machine_id === "M005" && "4 Bar Rotary Creasing"}
                  {activeEntry?.machine_id === "M006" && "RS4 Printing & Slotting"}
                  {activeEntry?.machine_id === "M007" && "Flat Bed Punching"}
                  {activeEntry?.machine_id === "M008" && "Rotary Die Punching"}
                  {activeEntry?.machine_id === "M009" && "Calico Pasting"}
                  {activeEntry?.machine_id === "M009A" && "Board Drying"}
                  {activeEntry?.machine_id === "M010" && "Stitching & Bundling"}
                  {activeEntry?.machine_id === "M011" && "Loading / Unloading"}
                  {activeEntry?.machine_id === "M012" && "Transport"}
                </span>
              </div>

              {/* M002 - Sub-Process Selection (Sheet Cutting or Paper Cutting) */}
              {activeEntry?.machine_id === "M002" && (
                <div className="rounded-xl border-2 border-slate-200 bg-slate-50 overflow-hidden">
                  <div className="px-4 py-3 bg-slate-100">
                    <p className="font-semibold text-gray-800 text-sm text-center">Select Cutting Process</p>
                  </div>
                  <div className="p-4 grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setM002SubProcess("sheet_cutting")}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        m002SubProcess === "sheet_cutting" 
                          ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200" 
                          : "border-gray-200 bg-white hover:border-blue-300"
                      }`}
                      data-testid="select-sheet-cutting"
                    >
                      <div className="text-center">
                        <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${
                          m002SubProcess === "sheet_cutting" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-600"
                        }`}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        </div>
                        <p className={`font-semibold text-sm ${m002SubProcess === "sheet_cutting" ? "text-blue-700" : "text-gray-700"}`}>
                          Sheet Cutting
                        </p>
                        <p className="text-xs text-gray-500">P2A</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setM002SubProcess("paper_cutting")}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        m002SubProcess === "paper_cutting" 
                          ? "border-cyan-500 bg-cyan-50 ring-2 ring-cyan-200" 
                          : "border-gray-200 bg-white hover:border-cyan-300"
                      }`}
                      data-testid="select-paper-cutting"
                    >
                      <div className="text-center">
                        <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${
                          m002SubProcess === "paper_cutting" ? "bg-cyan-500 text-white" : "bg-gray-200 text-gray-600"
                        }`}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                        </div>
                        <p className={`font-semibold text-sm ${m002SubProcess === "paper_cutting" ? "text-cyan-700" : "text-gray-700"}`}>
                          Paper Cutting
                        </p>
                        <p className="text-xs text-gray-500">P2B</p>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Sheet Cutting (P2A) - Only Sheet Count - M002A (New) or M002 with sub-process */}
              {(activeEntry?.machine_id === "M002A" || (activeEntry?.machine_id === "M002" && m002SubProcess === "sheet_cutting")) && (
                <div className="rounded-xl border-2 border-blue-200 bg-blue-50 overflow-hidden">
                  <div className="px-4 py-3 bg-blue-100 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-500 text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">Sheet Counting</p>
                      <p className="text-xs text-gray-500">Enter actual sheets cut</p>
                    </div>
                  </div>
                  <div className="p-4">
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      value={productionCounts.sheetCounting}
                      onChange={(e) => setProductionCounts(prev => ({...prev, sheetCounting: e.target.value}))}
                      placeholder="Enter sheet count"
                      className="w-full px-4 py-3 text-lg font-semibold text-center border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-0 focus:outline-none bg-white text-gray-900"
                      data-testid="sheet-counting-input"
                    />
                  </div>
                </div>
              )}

              {/* Paper Cutting (P2B) - Paper Count + Auto-calculated weight - M002B (New) or M002 with sub-process */}
              {(activeEntry?.machine_id === "M002B" || (activeEntry?.machine_id === "M002" && m002SubProcess === "paper_cutting")) && (
                <div className="rounded-xl border-2 border-cyan-200 bg-cyan-50 overflow-hidden">
                  <div className="px-4 py-3 bg-cyan-100 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-cyan-500 text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">Paper Cutting</p>
                      <p className="text-xs text-gray-500">Enter paper count — weight auto-calculated</p>
                    </div>
                  </div>
                  <div className="p-4 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Paper Counting</label>
                      <input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        value={productionCounts.paperCounting}
                        onChange={(e) => setProductionCounts(prev => ({...prev, paperCounting: e.target.value}))}
                        placeholder="Enter paper count"
                        className="w-full px-4 py-3 text-lg font-semibold text-center border-2 border-cyan-200 rounded-xl focus:border-cyan-500 focus:ring-0 focus:outline-none bg-white text-gray-900"
                        data-testid="paper-counting-input"
                      />
                    </div>
                    
                    {/* Auto-calculated weight breakdown */}
                    {p2bReelInfo && (
                      <div className="bg-white rounded-xl border border-cyan-200 p-3 space-y-2">
                        <p className="text-xs font-semibold text-cyan-700 uppercase tracking-wider">Auto-Calculated Weight</p>
                        <div className="grid grid-cols-3 gap-2 text-xs text-gray-500">
                          <div className="text-center">
                            <p className="font-medium">Reel Size</p>
                            <p className="text-gray-900 font-semibold">{p2bReelInfo.reelSize}</p>
                          </div>
                          <div className="text-center">
                            <p className="font-medium">Cutting Size</p>
                            <p className="text-gray-900 font-semibold">{p2bReelInfo.cuttingSize}</p>
                          </div>
                          <div className="text-center">
                            <p className="font-medium">GSM</p>
                            <p className="text-gray-900 font-semibold">{p2bReelInfo.gsm}</p>
                          </div>
                        </div>
                        {p2bCalc && (
                          <div className="space-y-2 pt-2 border-t border-cyan-100">
                            <p className="text-xs text-gray-500 font-mono">
                              Paper Wt: ({p2bReelInfo.reelSize} x {p2bReelInfo.cuttingSize} x {p2bReelInfo.gsm}) / 1550 = <span className="text-gray-900 font-bold">{p2bCalc.paperWeightKg} kg</span>
                            </p>
                            <p className="text-xs text-gray-500 font-mono">
                              Total papers possible: {p2bReelInfo.originalWeight} / {p2bCalc.paperWeightKg} = <span className="text-gray-900 font-bold">{p2bCalc.totalPapersPossible} Nos</span>
                            </p>
                            <p className="text-xs text-gray-500 font-mono">
                              Balance papers: {p2bCalc.totalPapersPossible} - {productionCounts.paperCounting} = <span className="text-gray-900 font-bold">{p2bCalc.balancePapers} Nos</span>
                            </p>
                            <div className="flex justify-between items-center bg-cyan-50 rounded-lg p-2 mt-1">
                              <div>
                                <p className="text-xs text-gray-500">Used Weight</p>
                                <p className="text-sm font-bold text-red-600">{p2bCalc.usedWeightKg} kg</p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-gray-500">Balance Reel Wt</p>
                                <p className="text-sm font-bold text-green-600">{p2bCalc.balanceWeightKg} kg</p>
                              </div>
                            </div>
                            <p className="text-xs text-cyan-600 text-center mt-1">
                              Bit reel will be auto-created with {p2bCalc.balanceWeightKg} kg ({p2bCalc.balancePapers} papers)
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    {!p2bReelInfo && (
                      <p className="text-xs text-amber-600 text-center">
                        Reel info not found — check WO reel assignment
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Board Counting - Only for M003 (Sheet Pasting) */}
              {activeEntry?.machine_id === "M003" && (
                <div className="rounded-xl border-2 border-purple-200 bg-purple-50 overflow-hidden">
                  <div className="px-4 py-3 bg-purple-100 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-purple-500 text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">Board Counting</p>
                      <p className="text-xs text-gray-500">Enter actual boards pasted</p>
                    </div>
                  </div>
                  <div className="p-4">
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      value={productionCounts.boardCountingP3}
                      onChange={(e) => setProductionCounts(prev => ({...prev, boardCountingP3: e.target.value}))}
                      placeholder="Enter count"
                      className="w-full px-4 py-3 text-lg font-semibold text-center border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:ring-0 focus:outline-none bg-white text-gray-900"
                      data-testid="board-counting-p3-input"
                    />
                  </div>
                </div>
              )}

              {/* Board Counting - For M004 (Thin Blade Slitter) */}
              {activeEntry?.machine_id === "M004" && (
                <div className="rounded-xl border-2 border-orange-200 bg-orange-50 overflow-hidden">
                  <div className="px-4 py-3 bg-orange-100 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-orange-500 text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">Board Counting</p>
                      <p className="text-xs text-gray-500">Enter actual boards processed</p>
                    </div>
                  </div>
                  <div className="p-4">
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      value={productionCounts.boardCountingP4}
                      onChange={(e) => setProductionCounts(prev => ({...prev, boardCountingP4: e.target.value}))}
                      placeholder="Enter count"
                      className="w-full px-4 py-3 text-lg font-semibold text-center border-2 border-orange-200 rounded-xl focus:border-orange-500 focus:ring-0 focus:outline-none bg-white text-gray-900"
                      data-testid="board-counting-p4-input"
                    />
                  </div>
                </div>
              )}

              {/* Board Counting - For M005 (4 Bar Rotary Creasing) */}
              {activeEntry?.machine_id === "M005" && (
                <div className="rounded-xl border-2 border-cyan-200 bg-cyan-50 overflow-hidden">
                  <div className="px-4 py-3 bg-cyan-100 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-cyan-500 text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">Board Counting</p>
                      <p className="text-xs text-gray-500">Enter actual boards processed</p>
                    </div>
                  </div>
                  <div className="p-4">
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      value={productionCounts.boardCountingP5}
                      onChange={(e) => setProductionCounts(prev => ({...prev, boardCountingP5: e.target.value}))}
                      placeholder="Enter count"
                      className="w-full px-4 py-3 text-lg font-semibold text-center border-2 border-cyan-200 rounded-xl focus:border-cyan-500 focus:ring-0 focus:outline-none bg-white text-gray-900"
                      data-testid="board-counting-p5-input"
                    />
                  </div>
                </div>
              )}

              {/* Board Counting - For M006 (RS4 Printing) */}
              {activeEntry?.machine_id === "M006" && (
                <div className="rounded-xl border-2 border-pink-200 bg-pink-50 overflow-hidden">
                  <div className="px-4 py-3 bg-pink-100 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-pink-500 text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">Board Counting</p>
                      <p className="text-xs text-gray-500">Enter actual boards printed</p>
                    </div>
                  </div>
                  <div className="p-4">
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      value={productionCounts.boardCountingP6}
                      onChange={(e) => setProductionCounts(prev => ({...prev, boardCountingP6: e.target.value}))}
                      placeholder="Enter count"
                      className="w-full px-4 py-3 text-lg font-semibold text-center border-2 border-pink-200 rounded-xl focus:border-pink-500 focus:ring-0 focus:outline-none bg-white text-gray-900"
                      data-testid="board-counting-p6-input"
                    />
                  </div>
                </div>
              )}

              {/* Board Counting - For M007 (Flat Bed Punching) */}
              {activeEntry?.machine_id === "M007" && (
                <div className="rounded-xl border-2 border-indigo-200 bg-indigo-50 overflow-hidden">
                  <div className="px-4 py-3 bg-indigo-100 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-indigo-500 text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">Board Counting</p>
                      <p className="text-xs text-gray-500">Enter actual boards punched</p>
                    </div>
                  </div>
                  <div className="p-4">
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      value={productionCounts.boardCountingP7}
                      onChange={(e) => setProductionCounts(prev => ({...prev, boardCountingP7: e.target.value}))}
                      placeholder="Enter count"
                      className="w-full px-4 py-3 text-lg font-semibold text-center border-2 border-indigo-200 rounded-xl focus:border-indigo-500 focus:ring-0 focus:outline-none bg-white text-gray-900"
                      data-testid="board-counting-p7-input"
                    />
                  </div>
                </div>
              )}

              {/* Board Counting - For M008 (Rotary Die Punching) */}
              {activeEntry?.machine_id === "M008" && (
                <div className="rounded-xl border-2 border-red-200 bg-red-50 overflow-hidden">
                  <div className="px-4 py-3 bg-red-100 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-red-500 text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">Board Counting</p>
                      <p className="text-xs text-gray-500">Enter actual boards punched</p>
                    </div>
                  </div>
                  <div className="p-4">
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      value={productionCounts.boardCountingP8}
                      onChange={(e) => setProductionCounts(prev => ({...prev, boardCountingP8: e.target.value}))}
                      placeholder="Enter count"
                      className="w-full px-4 py-3 text-lg font-semibold text-center border-2 border-red-200 rounded-xl focus:border-red-500 focus:ring-0 focus:outline-none bg-white text-gray-900"
                      data-testid="board-counting-p8-input"
                    />
                  </div>
                </div>
              )}

              {/* Total Boxes - For M010 (Stitching & Bundling) */}
              {activeEntry?.machine_id === "M010" && (
                <div className="rounded-xl border-2 border-teal-200 bg-teal-50 overflow-hidden">
                  <div className="px-4 py-3 bg-teal-100 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-teal-500 text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">Total Boxes</p>
                      <p className="text-xs text-gray-500">Enter total boxes stitched & bundled</p>
                    </div>
                  </div>
                  <div className="p-4">
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      value={productionCounts.totalBoxesP10}
                      onChange={(e) => setProductionCounts(prev => ({...prev, totalBoxesP10: e.target.value}))}
                      placeholder="Enter count"
                      className="w-full px-4 py-3 text-lg font-semibold text-center border-2 border-teal-200 rounded-xl focus:border-teal-500 focus:ring-0 focus:outline-none bg-white text-gray-900"
                      data-testid="total-boxes-p10-input"
                    />
                  </div>
                </div>
              )}

              {/* Board Drying - For M009A (Labor Cost Only) */}
              {activeEntry?.machine_id === "M009A" && (
                <div className="rounded-xl border-2 border-amber-200 bg-amber-50 overflow-hidden" data-testid="m009a-labor-section">
                  <div className="px-4 py-3 bg-amber-100 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-amber-500 text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">Board Drying - Labor Cost Only</p>
                      <p className="text-xs text-gray-500">No material consumed. Clock-in/Clock-out for labor tracking.</p>
                    </div>
                  </div>
                  <div className="p-4 text-center">
                    <p className="text-sm text-gray-600">Click confirm to complete this process and record labor time.</p>
                  </div>
                </div>
              )}

              {/* Corrugated Rolls Created - Only for Corrugation (M001) */}
              {activeEntry?.machine_id === "M001" && (
                <div className="rounded-xl border-2 border-amber-200 bg-amber-50 overflow-hidden">
                  <div className="px-4 py-3 bg-amber-100 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-amber-500 text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">Corrugated Rolls Created</p>
                      <p className="text-xs text-gray-500">Total rolls produced from parent reels</p>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        value={corrugatedRollsCreated}
                        onChange={(e) => setCorrugatedRollsCreated(e.target.value)}
                        placeholder="Enter count"
                        className="flex-1 px-4 py-3 text-lg font-semibold text-center border-2 border-amber-200 rounded-xl focus:border-amber-500 focus:ring-0 focus:outline-none bg-white text-gray-900"
                        data-testid="corrugated-rolls-input"
                      />
                      <span className="text-gray-600 font-medium">Rolls</span>
                    </div>
                    <p className="text-xs text-amber-600 text-center mt-2">
                      Enter the number of corrugated rolls produced
                    </p>
                  </div>
                </div>
              )}

              {/* Material Cards - Hidden for P2B (M002B or M002 paper cutting) since auto-calc handles it */}
              {!(activeEntry?.machine_id === "M002B" || (activeEntry?.machine_id === "M002" && m002SubProcess === "paper_cutting")) && materialTrackingData.map((row, idx) => (
                <div 
                  key={row.id} 
                  className={`rounded-xl border-2 overflow-hidden ${
                    row.isPrintedPaper 
                      ? 'border-purple-200 bg-purple-50' 
                      : row.isAssigned 
                        ? 'border-blue-200 bg-blue-50' 
                        : 'border-gray-200 bg-white'
                  }`}
                >
                  {/* Card Header */}
                  <div className={`px-4 py-3 flex items-center justify-between ${
                    row.isPrintedPaper 
                      ? 'bg-purple-100' 
                      : row.isAssigned 
                        ? 'bg-blue-100' 
                        : 'bg-gray-100'
                  }`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                        row.isPrintedPaper ? 'bg-purple-500' : 'bg-emerald-500'
                      }`}>
                        {idx + 1}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">
                          {row.reelId || "Select Reel"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {row.isPrintedPaper ? 'Printed Paper' : row.reelData?.rm_name || 'Raw Material'}
                        </p>
                      </div>
                    </div>
                    {materialTrackingData.length > 1 && !row.isAssigned && !row.isPrintedPaper && (
                      <button
                        onClick={() => removeMaterialRow(row.id)}
                        className="p-2 rounded-full hover:bg-red-100 text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Card Body */}
                  <div className="p-4 space-y-4">
                    {row.isPrintedPaper ? (
                      /* Printed Paper (Qty-based) */
                      <>
                        {/* Original Qty */}
                        <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <span className="text-sm text-gray-600">Original Qty</span>
                          <span className="text-lg font-bold text-purple-600">
                            {row.reelData?.qty || row.assignedQty || 0} Nos
                          </span>
                        </div>

                        {/* Input Fields */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Qty Used</label>
                            <input
                              type="number"
                              inputMode="numeric"
                              value={row.usedQty || ""}
                              onChange={(e) => updateMaterialRow(row.id, "usedQty", e.target.value)}
                              placeholder="0"
                              className="w-full px-4 py-3 text-lg font-semibold text-center border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:ring-0 focus:outline-none text-gray-900 bg-white"
                              data-testid={`used-qty-${idx}`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Balance Qty</label>
                            <div className="w-full px-4 py-3 text-lg font-semibold text-center bg-gray-100 rounded-xl text-gray-700" data-testid={`balance-qty-${idx}`}>
                              {row.balanceQty || "—"}
                            </div>
                          </div>
                        </div>
                        {parseFloat(row.balanceQty) > 0 && (
                          <p className="text-xs text-emerald-600 text-center">✓ New stock will be created for balance</p>
                        )}
                      </>
                    ) : row.isCorrugatedRoll ? (
                      /* Corrugated Roll (Qty-based) */
                      <>
                        {/* Total Rolls Available */}
                        <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                          <span className="text-sm text-gray-600">Total Rolls Available</span>
                          <span className="text-lg font-bold text-amber-600">
                            {row.reelData?.qty || row.assignedQty || 0} Nos
                          </span>
                        </div>

                        {/* Input Fields for Corrugated Roll */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Rolls Used</label>
                            <input
                              type="number"
                              inputMode="numeric"
                              min="0"
                              value={row.usedQty || ""}
                              onChange={(e) => updateMaterialRow(row.id, "usedQty", e.target.value)}
                              placeholder="0"
                              className="w-full px-4 py-3 text-lg font-semibold text-center border-2 border-amber-200 rounded-xl focus:border-amber-500 focus:ring-0 focus:outline-none text-gray-900 bg-white"
                              data-testid={`corrugated-rolls-used-${idx}`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Balance Rolls</label>
                            <input
                              type="number"
                              inputMode="numeric"
                              min="0"
                              value={row.balanceQty || ""}
                              onChange={(e) => updateMaterialRow(row.id, "balanceQty", e.target.value)}
                              placeholder="0"
                              className="w-full px-4 py-3 text-lg font-semibold text-center border-2 border-amber-200 rounded-xl focus:border-amber-500 focus:ring-0 focus:outline-none text-gray-900 bg-white"
                              data-testid={`corrugated-rolls-balance-${idx}`}
                            />
                          </div>
                        </div>
                        {parseFloat(row.balanceQty) > 0 && (
                          <p className="text-xs text-amber-600 text-center">✓ Balance rolls will be updated in inventory</p>
                        )}
                      </>
                    ) : row.isAssigned || row.reelId ? (
                      /* Assigned Reel (Weight-based) */
                      <>
                        {/* Original Weight */}
                        <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <span className="text-sm text-gray-600">Original Weight</span>
                          <span className="text-lg font-bold text-blue-600">
                            {row.reelData?.reel_weight || row.assignedWeight || 0} Kg
                          </span>
                        </div>

                        {/* Input Fields */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Balance (Kg)</label>
                            <input
                              type="number"
                              inputMode="decimal"
                              step="0.1"
                              value={row.balanceWeight || ""}
                              onChange={(e) => updateMaterialRow(row.id, "balanceWeight", e.target.value)}
                              placeholder="0"
                              className="w-full px-4 py-3 text-lg font-semibold text-center border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:ring-0 focus:outline-none text-gray-900 bg-white"
                              data-testid={`balance-weight-${idx}`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Used (Kg)</label>
                            <div className="w-full px-4 py-3 text-lg font-semibold text-center bg-gray-100 rounded-xl text-gray-700" data-testid={`used-weight-${idx}`}>
                              {row.usedWeight || "—"}
                            </div>
                          </div>
                        </div>
                        {parseFloat(row.balanceWeight) > 0 && (
                          <p className="text-xs text-emerald-600 text-center">✓ Bit reel will be created for balance</p>
                        )}

                        {/* Reel Details */}
                        {row.reelData && (
                          <div className="flex flex-wrap gap-2 pt-2 border-t">
                            <span className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                              Mill: {row.reelData.mill_name || "N/A"}
                            </span>
                            <span className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                              Size: {row.reelData.reel_size || "N/A"}"
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      /* Reel Selection */
                      <>
                        <Select
                          value={row.reelId || "placeholder"}
                          onValueChange={(value) => updateMaterialRow(row.id, "reelId", value)}
                        >
                          <SelectTrigger className="h-12 text-base">
                            <SelectValue placeholder="Tap to select reel..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="placeholder" disabled>Select a reel...</SelectItem>
                            {workOrderReels.map((reel) => (
                              <SelectItem key={reel.id} value={reel.reel_id}>
                                {reel.reel_id} ({reel.reel_weight || 0} kg)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {row.reelId && (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Balance (Kg)</label>
                              <input
                                type="number"
                                inputMode="decimal"
                                step="0.1"
                                value={row.balanceWeight}
                                onChange={(e) => updateMaterialRow(row.id, "balanceWeight", e.target.value)}
                                placeholder="0"
                                className="w-full px-4 py-3 text-lg font-semibold text-center border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:ring-0 focus:outline-none text-gray-900 bg-white"
                                data-testid={`balance-weight-${idx}`}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Used (Kg)</label>
                              <div className="w-full px-4 py-3 text-lg font-semibold text-center bg-gray-100 rounded-xl text-gray-700" data-testid={`used-weight-${idx}`}>
                                {row.usedWeight || "—"}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}

              {/* Summary Card */}
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-emerald-700">Total Materials</span>
                  <span className="font-bold text-emerald-800">{materialTrackingData.filter(r => r.reelId).length}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-emerald-700">Bit Reels to Create</span>
                  <span className="font-bold text-emerald-800">{materialTrackingData.filter(r => parseFloat(r.balanceWeight) > 0 && !r.isPrintedPaper).length}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-emerald-700">Balance Stock</span>
                  <span className="font-bold text-emerald-800">{materialTrackingData.filter(r => parseFloat(r.balanceQty) > 0 && r.isPrintedPaper).length}</span>
                </div>
                {activeEntry?.machine_id === "M001" && corrugatedRollsCreated && (
                  <div className="flex items-center justify-between text-sm mt-1 pt-1 border-t border-emerald-200">
                    <span className="text-amber-700">Corrugated Rolls</span>
                    <span className="font-bold text-amber-800">{corrugatedRollsCreated}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Fixed Bottom Actions */}
            <div className="p-4 bg-white border-t border-gray-200 space-y-2">
              <Button
                onClick={submitMaterialTracking}
                disabled={loading}
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white text-base font-semibold rounded-xl"
              >
                {loading ? "Saving..." : "✓ Save & Complete"}
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => {
                    setShowMaterialTrackingDialog(false);
                    setProductionCounts({
                      sheetCounting: "",
                      paperCounting: "",
                      balanceReelWeight: "",
                      boardCountingP3: "",
                      boardCountingP4: "",
                      boardCountingP5: "",
                      boardCountingP6: "",
                      boardCountingP7: "",
                      boardCountingP8: "",
                      totalBoxesP10: ""
                    });
                    setM002SubProcess("sheet_cutting");
                    finalizeStop();
                  }}
                  variant="outline"
                  className="h-10 rounded-xl text-sm"
                >
                  Skip (No Count)
                </Button>
                <Button
                  onClick={() => {
                    setShowMaterialTrackingDialog(false);
                    setShowStatusDialog(true);
                    setPendingStopData(null);
                    setCorrugatedRollsCreated("");
                    setProductionCounts({
                      sheetCounting: "",
                      paperCounting: "",
                      balanceReelWeight: "",
                      boardCountingP3: "",
                      boardCountingP4: "",
                      boardCountingP5: "",
                      boardCountingP6: "",
                      boardCountingP7: "",
                      boardCountingP8: "",
                      totalBoxesP10: ""
                    });
                    setM002SubProcess("sheet_cutting");
                  }}
                  variant="ghost"
                  className="h-10 rounded-xl text-sm text-gray-500"
                >
                  ← Back
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeTimesheet;